import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { SimulationRoomProps, Turn } from '../types';
import { useSimulationSession } from '../hooks/useSimulationSession';
import { useCameraRecorder } from '../../video/hooks/useCameraRecorder';
import { useLocalFaceAnalysis } from '../../video';
import { SpeechControlledAvatar, getPersonaAvatarSVG } from './SpeechControlledAvatar';
import { useAuth } from '../../../stores/authStore';
import { SystemCheckModal } from '../../readiness';
import { stopAllSpeech, isFemaleName, clearPersonaVoiceCache } from '../../../services/voiceService';
import { useTranslation } from '../../../i18n';

const FEMALE_DEFAULT_AVATAR = 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=600&q=80';
const MALE_DEFAULT_AVATAR = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80';

const translateRole = (role?: string | null, tFunc?: (k: string, opt?: any, def?: string) => string) => {
  if (!role) return '';
  const r = role.toLowerCase();
  if (tFunc) {
    if (r.includes('customer')) return tFunc('role.customer', undefined, 'Customer');
    if (r.includes('client')) return tFunc('role.client', undefined, 'Client');
    if (r.includes('manager')) return tFunc('role.manager', undefined, 'Manager');
    if (r.includes('colleague') || r.includes('peer')) return tFunc('role.colleague', undefined, 'Colleague');
    if (r.includes('learner') || r.includes('user')) return tFunc('role.learner', undefined, 'Learner');
  }
  return role;
};

const translateMood = (mood?: string | null, tFunc?: (k: string, opt?: any, def?: string) => string) => {
  if (!mood) return '';
  const m = mood.toLowerCase();
  if (tFunc) {
    if (m.includes('irritated') && m.includes('polite')) return tFunc('mood.irritated_polite', undefined, 'Mildly irritated but polite');
    if (m.includes('frustrated')) return tFunc('mood.frustrated', undefined, 'Frustrated');
    if (m.includes('satisfied')) return tFunc('mood.satisfied', undefined, 'Satisfied');
    if (m.includes('neutral')) return tFunc('mood.neutral', undefined, 'Neutral');
    if (m.includes('anxious') || m.includes('nervous')) return tFunc('mood.anxious', undefined, 'Anxious');
    if (m.includes('demanding')) return tFunc('mood.demanding', undefined, 'Demanding');
  }
  return mood;
};

export const SimulationRoom: React.FC<SimulationRoomProps> = (props) => {
  const { scenarioId: urlScenarioId } = useParams<{ scenarioId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isRetry = searchParams.get('retry') === 'true';
  const urlAttemptId = searchParams.get('attemptId') || undefined;
  const urlLang = searchParams.get('lang') || undefined;
  const storedLang = typeof window !== 'undefined' ? sessionStorage.getItem('trainova_preferred_language') : null;
  const rawLang = props.language || urlLang || storedLang || 'en';
  const effectiveLanguage = typeof rawLang === 'string' && rawLang.startsWith('hi') ? 'hi' : rawLang;

  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window !== 'undefined' && effectiveLanguage) {
      sessionStorage.setItem('trainova_preferred_language', effectiveLanguage);
    }
  }, [effectiveLanguage]);

  const targetScenarioId = props.scenarioId || urlScenarioId;
  const targetAttemptId = props.attemptId || urlAttemptId;

  // Guard: Every simulation attempt must originate from the briefing page
  useEffect(() => {
    if (targetScenarioId && !targetAttemptId) {
      const langParam = effectiveLanguage ? `?lang=${effectiveLanguage}` : '';
      navigate(`/simulations/${targetScenarioId}/briefing${langParam}`, { replace: true });
    }
  }, [targetScenarioId, targetAttemptId, effectiveLanguage, navigate]);

  const [isScenarioOpen, setIsScenarioOpen] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [copiedTrace, setCopiedTrace] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isTranscriptDrawerOpen, setIsTranscriptDrawerOpen] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [dismissedMicBanner, setDismissedMicBanner] = useState(false);
  const [showMicHelp, setShowMicHelp] = useState(false);
  const [dismissedAudioError, setDismissedAudioError] = useState(false);
  const [showManualTextInput, setShowManualTextInput] = useState(false);
  const isRepeatingTurnRef = useRef(false);
  const transcriptDrawerEndRef = useRef<HTMLDivElement>(null);
  const interimScrollScheduledRef = useRef(false);
  const drawerInterimScrollScheduledRef = useRef(false);

  // Pre-flight System Readiness gate
  const [preflightVerified, setPreflightVerified] = useState<boolean>(Boolean(targetAttemptId) || isRetry);
  const [showPreflightModal, setShowPreflightModal] = useState<boolean>(!targetAttemptId && !isRetry);
  const [scenarioRequirements, setScenarioRequirements] = useState<{
    requiresCamera?: boolean;
    requiresMicrophone?: boolean;
    requiresSpeaker?: boolean;
    scenarioTitle?: string;
  }>({
    requiresCamera: true,
    requiresMicrophone: true,
    requiresSpeaker: true,
  });

  const [initTimeoutElapsed, setInitTimeoutElapsed] = useState(false);
  // Voice check runs while the room is still loading. Keeping it as explicit state lets
  // the entry screen show "checking / ready / blocked" instead of a step that silently
  // stays on "Waiting" until the first persona turn asks for the microphone.
  const [voiceCheckState, setVoiceCheckState] = useState<'idle' | 'checking' | 'ready' | 'blocked'>('idle');
  const [isRetryingVoice, setIsRetryingVoice] = useState(false);
  const voiceCheckStartedRef = useRef(false);
  const [showStuckExit, setShowStuckExit] = useState(false);

  // Cancel speech synthesis utterance when unmounting the simulation room
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Automatically check if browser permissions are already granted
  useEffect(() => {
    if (isRetry) {
      setPreflightVerified(true);
      setShowPreflightModal(false);
      return;
    }
    let cancelled = false;
    async function checkExistingPermissions() {
      try {
        // If neither microphone nor camera is required, automatically verify preflight
        if (scenarioRequirements.requiresMicrophone === false && scenarioRequirements.requiresCamera === false) {
          if (!cancelled) {
            setPreflightVerified(true);
            setShowPreflightModal(false);
          }
          return;
        }

        if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
          const micPerm = scenarioRequirements.requiresMicrophone !== false
            ? await navigator.permissions.query({ name: 'microphone' as any }).catch(() => null)
            : { state: 'granted' as const };
          const camPerm = scenarioRequirements.requiresCamera !== false
            ? await navigator.permissions.query({ name: 'camera' as any }).catch(() => null)
            : { state: 'granted' as const };

          const micGranted = scenarioRequirements.requiresMicrophone === false || micPerm?.state === 'granted';
          const camGranted = scenarioRequirements.requiresCamera === false || camPerm?.state === 'granted';

          if (micGranted && camGranted) {
            if (!cancelled) {
              setPreflightVerified(true);
              setShowPreflightModal(false);
              if (scenarioRequirements.requiresCamera !== false) {
                setIsCameraOn(true);
              }
              return;
            }
          }
        }
      } catch {
        // Continue with normal modal prompt if permission query is not supported
      }
    }

    checkExistingPermissions();
    return () => { cancelled = true; };
  }, [scenarioRequirements.requiresCamera, scenarioRequirements.requiresMicrophone, targetAttemptId]);

  // Auto-activate camera once preflight is verified and camera is permitted
  useEffect(() => {
    if (preflightVerified && scenarioRequirements.requiresCamera !== false) {
      setIsCameraOn(true);
    }
  }, [preflightVerified, scenarioRequirements.requiresCamera]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  const recorderRef = useRef<any>(null);

  // Master Session Hook (lifecycle loads session/attempt; audio active once preflight is verified)
  const session = useSimulationSession({
    ...props,
    scenarioId: targetScenarioId,
    attemptId: targetAttemptId,
    language: effectiveLanguage,
    autoPlayInitialTurn: false,
    enabled: true,
    initialAttemptData: props.initialAttemptData || (location.state as any)?.initialAttemptData,
    onEndSimulation: async (turns, finishedAttemptId) => {
      // 0. Finalize session video/audio recording
      if (recorderRef.current && typeof recorderRef.current.stopRecording === 'function') {
        try {
          await Promise.race([
            recorderRef.current.stopRecording(),
            new Promise((resolve) => setTimeout(resolve, 15000)),
          ]);
        } catch (recErr) {
          console.warn('[SimulationRoom] onEndSimulation recorder stop notice:', recErr);
        }
      }

      // 1. Turn off camera and stop all video tracks immediately
      setIsCameraOn(false);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // 2. Stop microphone listening (STT), speech synthesis (TTS), and Web Audio streams
      if (typeof session.stopAll === 'function') {
        session.stopAll();
      }
      if (session.audio?.stopAll) {
        session.audio.stopAll();
      }
      stopAllSpeech();
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        try {
          window.speechSynthesis.cancel();
        } catch { }
      }

      const idToNavigate = finishedAttemptId || session.attemptId || targetAttemptId;
      if (props.onEndSimulation) {
        props.onEndSimulation(turns, idToNavigate);
      } else {
        if (idToNavigate) {
          navigate(`/attempts/${idToNavigate}/results`);
        } else {
          navigate('/attempts');
        }
      }
    },
  });

  // Audio asset loading gate: required audio assets must be loaded & decoded before room initialization completes
  const [isOpeningAudioReady, setIsOpeningAudioReady] = useState(false);
  const dialogueEngineDone = session.state !== 'initializing' && session.state !== 'starting';
  const audioAssetPhaseDone = isOpeningAudioReady || session.turns.length > 1 || (dialogueEngineDone && session.turns.length === 0);
  const audioPreloadAttemptedRef = useRef<string | null>(null);
  const spokenAttemptIdRef = useRef<string | null>(null);
  const spokenAttemptIdsRef = useRef<Set<string>>(new Set());

  // Proactively release hardware if simulation completes or enters terminal state
  useEffect(() => {
    if (session.state === 'completing' || session.state === 'completed') {
      setIsCameraOn(false);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      if (typeof session.stopAll === 'function') {
        session.stopAll();
      }
      stopAllSpeech();
    }
  }, [session.state]);

  // Background voice readiness: prove microphone access while the briefing and dialogue
  // engine load, so the permission prompt never lands after the persona starts speaking.
  useEffect(() => {
    if (!preflightVerified || showPreflightModal || session.error) return;
    if (voiceCheckStartedRef.current) return;
    voiceCheckStartedRef.current = true;
    setVoiceCheckState('checking');
    void session
      .ensureMicrophone()
      .then((ok) => setVoiceCheckState(ok ? 'ready' : 'blocked'))
      .catch(() => setVoiceCheckState('blocked'));
  }, [preflightVerified, showPreflightModal, session.error, session.ensureMicrophone]);

  // The in-room mic gate can resolve the permission too (opening turn, manual retry);
  // mirror it so the entry screen never reports "blocked" once the mic is live.
  useEffect(() => {
    if (session.micReady || session.isListening) {
      setVoiceCheckState('ready');
    }
  }, [session.micReady, session.isListening]);

  // Evaluation loader watchdog: after 12s give the learner a way out instead of leaving
  // them on an overlay with no exit while the scorecard is still being prepared.
  useEffect(() => {
    const isPreparing = isEndingSession || session.state === 'completing' || session.state === 'completed';
    if (!isPreparing) {
      setShowStuckExit(false);
      return;
    }
    const timer = setTimeout(() => setShowStuckExit(true), 12000);
    return () => clearTimeout(timer);
  }, [isEndingSession, session.state]);

  // Watchdog timer: If initializing takes > 6 seconds after preflight is dismissed, display re-connect actions
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    const isInitializing = (session?.isLoadingScenario || !audioAssetPhaseDone) && !showPreflightModal;
    if (isInitializing) {
      timer = setTimeout(() => {
        setInitTimeoutElapsed(true);
      }, 6000);
    } else {
      setInitTimeoutElapsed(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [session?.isLoadingScenario, audioAssetPhaseDone, showPreflightModal]);

  // Sync scenario hardware requirements from session.briefing without redundant duplicate HTTP requests
  useEffect(() => {
    const briefingPayload = session.briefing;
    if (!briefingPayload) return;
    const sim = (briefingPayload as any)?.simulation || briefingPayload || {};

    const reqCamera = sim.requiresCamera !== undefined
      ? Boolean(sim.requiresCamera)
      : ((briefingPayload as any)?.requiresCamera !== undefined
        ? Boolean((briefingPayload as any).requiresCamera)
        : ((briefingPayload as any)?.prerequisiteGate?.requiresCamera !== undefined
          ? Boolean((briefingPayload as any).prerequisiteGate.requiresCamera)
          : (sim.hardwareRequirements?.requiresCamera !== undefined
            ? Boolean(sim.hardwareRequirements.requiresCamera)
            : true)));

    const reqMicrophone = sim.requiresMicrophone !== undefined
      ? Boolean(sim.requiresMicrophone)
      : ((briefingPayload as any)?.requiresMicrophone !== undefined
        ? Boolean((briefingPayload as any).requiresMicrophone)
        : ((briefingPayload as any)?.prerequisiteGate?.requiresMicrophone !== undefined
          ? Boolean((briefingPayload as any).prerequisiteGate.requiresMicrophone)
          : (sim.hardwareRequirements?.requiresMicrophone !== undefined
            ? Boolean(sim.hardwareRequirements.requiresMicrophone)
            : true)));

    const reqSpeaker = sim.requiresSpeaker !== undefined
      ? Boolean(sim.requiresSpeaker)
      : ((briefingPayload as any)?.requiresSpeaker !== undefined
        ? Boolean((briefingPayload as any).requiresSpeaker)
        : ((briefingPayload as any)?.prerequisiteGate?.requiresSpeaker !== undefined
          ? Boolean((briefingPayload as any).prerequisiteGate.requiresSpeaker)
          : (sim.hardwareRequirements?.requiresSpeaker !== undefined
            ? Boolean(sim.hardwareRequirements.requiresSpeaker)
            : true)));

    setScenarioRequirements({
      requiresCamera: reqCamera,
      requiresMicrophone: reqMicrophone,
      requiresSpeaker: reqSpeaker,
      scenarioTitle: sim.title || (briefingPayload as any)?.title,
    });

    // If none of the hardware requirements are compulsory (all unticked), auto-bypass preflight gate
    if (!reqCamera && !reqMicrophone && !reqSpeaker) {
      setPreflightVerified(true);
      setShowPreflightModal(false);
    }

    if (reqCamera && preflightVerified) {
      setIsCameraOn(true);
    }
  }, [session.briefing, preflightVerified]);

  const effectiveAttemptId = targetAttemptId || session.attemptId;
  const isAudioOnlyScenario = !isCameraOn;
  const [cameraStreamState, setCameraStreamState] = useState<MediaStream | null>(null);

  const isMobileClient = typeof navigator !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '') ||
    (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent || ''))
  );

  // Ensure both camera (when enabled) and mixed audio streams are ready before initiating recorder
  const isCameraReady = !isCameraOn || Boolean(cameraStreamState);
  const isAudioReady = !scenarioRequirements.requiresMicrophone ||
    voiceCheckState === 'ready' ||
    voiceCheckState === 'blocked' ||
    Boolean(session.mixedAudioStream) ||
    Boolean(session.audioStream);
  const recorderAutoStart = preflightVerified && isCameraReady && isAudioReady;

  // Meeting-style recording hook for persistent assessment capture (mixed audio + camera video when enabled)
  const recorder = useCameraRecorder({
    attemptId: effectiveAttemptId,
    scenarioId: targetScenarioId,
    captureMode: isCameraOn ? 'AUDIO_VIDEO' : 'AUDIO_ONLY',
    cameraStream: cameraStreamState,
    audioStream: session.mixedAudioStream || session.audioStream,
    autoStart: recorderAutoStart,
  });
  recorderRef.current = recorder;

  // Local browser-based face analysis using face-api.js (runs only when organization & simulation permit it)
  const faceAnalysis = useLocalFaceAnalysis({
    attemptId: effectiveAttemptId,
    enabled: Boolean(session.videoAnalysisEnabled && isCameraOn),
    videoElement: videoRef.current,
    cameraStream: cameraStreamState,
    config: session.videoAnalysisConfig,
    modelBaseUrl: '/models/face-api',
  });

  // Handle webcam video streaming
  useEffect(() => {
    let active = true;

    async function setupCamera() {
      if (isCameraOn) {
        if (!navigator?.mediaDevices?.getUserMedia) {
          console.warn('Camera requires a secure context (HTTPS or localhost). navigator.mediaDevices is unavailable.');
          setIsCameraOn(false);
          setCameraStreamState(null);
          return;
        }
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: isMobileClient ? 'user' : undefined },
            audio: false,
          });
          if (!active) {
            stream.getTracks().forEach((t) => t.stop());
            setCameraStreamState(null);
            return;
          }
          mediaStreamRef.current = stream;
          setCameraStreamState(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => { });
          }
        } catch (err) {
          console.warn('Camera access denied or unavailable:', err);
          setIsCameraOn(false);
          setCameraStreamState(null);
        }
      } else {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((t) => t.stop());
          mediaStreamRef.current = null;
        }
        setCameraStreamState(null);
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    }

    setupCamera();

    return () => {
      active = false;
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      setCameraStreamState(null);
    };
  }, [isCameraOn]);

  // Ensure mounted video element immediately attaches active webcam stream
  useEffect(() => {
    if (isCameraOn && mediaStreamRef.current && videoRef.current && videoRef.current.srcObject !== mediaStreamRef.current) {
      videoRef.current.srcObject = mediaStreamRef.current;
      videoRef.current.play().catch(() => { });
    }
  });

  // Master unmount & navigation cleanup: stop recording, media tracks, STT, and TTS immediately
  useEffect(() => {
    return () => {
      // 1. Release media tracks (camera & microphone)
      setIsCameraOn(false);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      // 2. Stop camera/screen recording
      try {
        recorder.stopRecording().catch(() => { });
      } catch { }
      // 3. Stop audio listening (STT) and voice output (TTS)
      if (typeof session.stopAll === 'function') {
        session.stopAll();
      }
      if (session.audio?.stopAll) {
        session.audio.stopAll();
      }
      stopAllSpeech();
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Auto-scroll transcript on update safely.
  // Committed turns scroll smoothly; interim STT updates scroll instantly so
  // overlapping smooth animations cannot jitter the layout on mobile.
  useEffect(() => {
    if (transcriptEndRef.current && document.body.contains(transcriptEndRef.current)) {
      try {
        transcriptEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch { }
    }
  }, [session.turns]);

  useEffect(() => {
    if (!session.interimTranscript) return;
    if (interimScrollScheduledRef.current) return;
    interimScrollScheduledRef.current = true;
    requestAnimationFrame(() => {
      interimScrollScheduledRef.current = false;
      if (transcriptEndRef.current && document.body.contains(transcriptEndRef.current)) {
        try {
          transcriptEndRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        } catch { }
      }
    });
  }, [session.interimTranscript]);

  // Compute derived metadata
  const bData: any = session.briefing || {};
  const simData: any = bData.simulation || bData;

  const scenarioTitle = simData.title || session.scenarioTitle || scenarioRequirements.scenarioTitle || 'AI Conversational Simulation';
  const category = simData.category || bData.category || 'Assessment';
  // The briefing payload exposes the running attempt under activeAttempt (top level and
  // inside prerequisiteGate), not as a flat attemptNumber, so a resumed attempt used to
  // render as "Attempt 1" while the briefing card correctly said Attempt #3.
  const attemptNum =
    bData.attemptNumber ||
    bData.activeAttempt?.attemptNumber ||
    bData.prerequisiteGate?.activeAttempt?.attemptNumber ||
    1;
  const maxAttempts = simData.maxAttempts || bData.maxAttempts || 3;

  // Derive comprehensive list of all added personas for multi-persona simulation
  const personasList: any[] = useMemo(() => {
    const rawList =
      (Array.isArray(simData.personas) && simData.personas.length > 0 ? simData.personas : null) ||
      (Array.isArray(bData.personas) && bData.personas.length > 0 ? bData.personas : null) ||
      (simData.persona ? [simData.persona] : []) ||
      (bData.persona ? [bData.persona] : []) ||
      [];
    const valid = rawList.filter((p: any) => p && (p.name || p.role || p.avatarUrl));
    return valid.length > 0
      ? valid.map((p: any, idx: number) => {
        const effectiveGender = p.genderPreference || simData.genderPreference || bData.genderPreference || (isFemaleName(p.name) ? 'female' : 'male');
        const isFemale = effectiveGender === 'female';
        const defaultAvatar = isFemale ? FEMALE_DEFAULT_AVATAR : MALE_DEFAULT_AVATAR;
        return {
          id: p.id || `persona-${idx}`,
          name: p.name || `AI Persona ${idx + 1}`,
          role: p.role || p.title || 'Participant',
          mood: p.mood || p.startingMood || 'Neutral',
          genderPreference: effectiveGender,
          avatarUrl: p.avatarUrl || p.imageUrl || p.avatar || simData.avatarUrl || bData.avatarUrl || defaultAvatar,
          avatarType: p.avatarType || simData.avatarType || bData.avatarType || 'image',
          country: p.country || simData.country || bData.country || 'IN',
          countryName: p.countryName || simData.countryName || bData.countryName || 'India',
          tone: p.tone || simData.tone || bData.tone || 'Empathetic & Cooperative',
          ttsVoice: p.ttsVoice || simData.ttsVoice || bData.ttsVoice || session.voiceContract?.ttsVoice || '',
          // The attempt's locked contract outranks a locally derived engine, so an
          // absent field can never quietly downgrade cloud synthesis to the browser.
          ttsProvider: p.ttsProvider || simData.ttsProvider || bData.ttsProvider || session.voiceContract?.ttsProvider || session.effectiveTTSProvider || 'browser',
          speakingPace: p.speakingPace !== undefined ? p.speakingPace : (simData.speakingPace !== undefined ? simData.speakingPace : 1.0),
          pitchPreference: p.pitchPreference !== undefined ? p.pitchPreference : (simData.pitchPreference !== undefined ? simData.pitchPreference : 1.0),
          traits: p.traits || p.behavioralTraits || [],
          background: p.background || p.prompt || '',
        };
      })
      : [
        {
          id: 'persona-0',
          name: session.personaName || simData.targetPersona || bData.targetPersona || 'AI Persona',
          role: simData.targetPersonaRole || bData.targetPersonaRole || 'Participant',
          mood: simData.startingMood || bData.startingMood || 'Neutral',
          genderPreference: simData.genderPreference || bData.genderPreference || (isFemaleName(session.personaName || simData.targetPersona) ? 'female' : 'male'),
          avatarUrl: simData.avatarUrl || bData.avatarUrl || (isFemaleName(session.personaName || simData.targetPersona) ? FEMALE_DEFAULT_AVATAR : MALE_DEFAULT_AVATAR),
          avatarType: simData.avatarType || bData.avatarType || 'image',
          country: simData.country || bData.country || 'IN',
          countryName: simData.countryName || bData.countryName || 'India',
          tone: simData.tone || bData.tone || 'Empathetic & Cooperative',
          ttsVoice: simData.ttsVoice || bData.ttsVoice || session.voiceContract?.ttsVoice || '',
          ttsProvider: simData.ttsProvider || bData.ttsProvider || session.voiceContract?.ttsProvider || session.effectiveTTSProvider || 'browser',
          speakingPace: simData.speakingPace !== undefined ? simData.speakingPace : 1.0,
          pitchPreference: simData.pitchPreference !== undefined ? simData.pitchPreference : 1.0,
          traits: [],
          background: '',
        },
      ];
  }, [simData, bData, session.personaName, session.voiceContract, session.effectiveTTSProvider]);

  const [activePersonaIdx, setActivePersonaIdx] = useState(0);
  const activePersona = personasList[activePersonaIdx] || personasList[0];
  const personaName = activePersona.name;
  const personaRole = activePersona.role;
  const personaMood = activePersona.mood;

  // Clear any stale cached persona voice for fresh simulation session
  useEffect(() => {
    clearPersonaVoiceCache(personaName);
  }, [personaName]);

  const hasSpokenOpeningTurnRef = useRef(false);

  // Preload and decode opening turn audio asset before allowing room playback or dismissing loading gate
  useEffect(() => {
    // If the attempt already has historical turns (resumed in-progress attempt), audio gate is instantly ready
    if (session.turns.length > 1) {
      setIsOpeningAudioReady(true);
      return;
    }

    // If there are no turns yet and dialogue engine is ready, opening audio gate is satisfied
    if (session.turns.length === 0) {
      if (session.state === 'ready' || session.state === 'listening') {
        setIsOpeningAudioReady(true);
      }
      return;
    }

    if (session.turns.length === 1) {
      const firstTurn = session.turns[0];
      const audioUrl = firstTurn.audioUrl;
      if (audioUrl) {
        if (audioPreloadAttemptedRef.current === audioUrl) {
          setIsOpeningAudioReady(true);
          return;
        }
        audioPreloadAttemptedRef.current = audioUrl;

        void session.preloadAndDecodeAudio(audioUrl)
          .then(() => setIsOpeningAudioReady(true))
          .catch(() => setIsOpeningAudioReady(true));
      } else {
        // Speech synthesis or text-only turn is ready as soon as dialogue engine is connected
        setIsOpeningAudioReady(true);
      }
    }
  }, [session.turns, session.state, session.preloadAndDecodeAudio]);

  // Track attempt transitions: if attempt changes (e.g. retry / new attempt ID), reset tracking for the new attempt
  useEffect(() => {
    const currentId = session.attemptId || targetAttemptId;
    if (currentId && currentId !== spokenAttemptIdRef.current) {
      spokenAttemptIdRef.current = currentId;
      if (!spokenAttemptIdsRef.current.has(currentId)) {
        hasSpokenOpeningTurnRef.current = false;
      }
    }
  }, [session.attemptId, targetAttemptId]);

  // Strict loading gate: no audio playback or autoplay until the scenario, briefing, and player are fully ready
  useEffect(() => {
    const currentAttemptId = session.attemptId || targetAttemptId;

    const isLifecycleReady =
      session.state === 'ready' ||
      session.state === 'listening' ||
      session.state === 'starting';

    const isRoomPlaybackReady =
      preflightVerified &&
      !showPreflightModal &&
      !session.isLoadingScenario &&
      audioAssetPhaseDone &&
      isLifecycleReady &&
      Boolean(session.briefing);

    // If the attempt already has historical turns (resumed in-progress attempt),
    // mark spokenAttemptIdsRef as currentAttemptId to prevent replay of the opening turn.
    if (session.turns.length > 1) {
      if (currentAttemptId) {
        spokenAttemptIdsRef.current.add(currentAttemptId);
      }
      hasSpokenOpeningTurnRef.current = true;

      // Resumed attempt: immediately activate listening if the AI is not speaking
      if (
        isRoomPlaybackReady &&
        !session.isAiSpeaking &&
        !session.isListening &&
        !session.isAudioLoading &&
        !session.isMuted &&
        (session.state === 'ready' || session.state === 'listening' || session.state === 'starting')
      ) {
        session.startListening();
      }
      return;
    }

    const alreadySpokenForThisAttempt = Boolean(
      (currentAttemptId && spokenAttemptIdsRef.current.has(currentAttemptId)) ||
      hasSpokenOpeningTurnRef.current
    );

    if (isRoomPlaybackReady && session.turns.length === 1 && !alreadySpokenForThisAttempt) {
      const firstTurn = session.turns[0];
      const spk = (firstTurn.speaker || (firstTurn as any).role || '').toLowerCase();
      if (!['learner', 'user', 'me', 'student'].includes(spk) && (firstTurn.text || firstTurn.audioUrl)) {
        hasSpokenOpeningTurnRef.current = true;
        if (currentAttemptId) {
          spokenAttemptIdsRef.current.add(currentAttemptId);
        }

        session.speakText(firstTurn.text, firstTurn.audioUrl, undefined, {
          personaName: firstTurn.personaName || personaName,
          // Per-turn backend fields first, then the attempt contract, then the
          // persona configuration — never a bare guess, which is what made the
          // opening line sound different from every reply after it.
          ttsVoice: firstTurn.ttsVoice || session.voiceContract?.ttsVoice || activePersona.ttsVoice,
          ttsProvider: firstTurn.ttsProvider || session.voiceContract?.ttsProvider || activePersona.ttsProvider,
          country: firstTurn.country || activePersona.country,
          tone: firstTurn.tone || activePersona.tone,
          pitch: firstTurn.pitchPreference !== undefined ? firstTurn.pitchPreference : activePersona.pitchPreference,
          rate: firstTurn.speakingPace !== undefined ? firstTurn.speakingPace : activePersona.speakingPace,
          genderPreference: firstTurn.genderPreference || session.voiceContract?.genderPreference || activePersona.genderPreference,
        });
      } else {
        // First turn is from learner or has no speech: activate listening
        if (
          !session.isAiSpeaking &&
          !session.isListening &&
          !session.isAudioLoading &&
          !session.isMuted &&
          (session.state === 'ready' || session.state === 'listening' || session.state === 'starting')
        ) {
          session.startListening();
        }
      }
    } else if (
      isRoomPlaybackReady &&
      alreadySpokenForThisAttempt &&
      !session.isAiSpeaking &&
      !session.isListening &&
      !session.isAudioLoading &&
      !session.isMuted &&
      (session.state === 'ready' || session.state === 'listening' || session.state === 'starting')
    ) {
      session.startListening();
    }
  }, [
    preflightVerified,
    showPreflightModal,
    session.isLoadingScenario,
    audioAssetPhaseDone,
    session.state,
    session.briefing,
    session.turns,
    session.attemptId,
    targetAttemptId,
    personaName,
    activePersona,
    session.voiceContract,
    session.speakText,
    session.isAiSpeaking,
    session.isListening,
    session.isAudioLoading,
    session.isAutoplayBlocked,
    session.isMuted,
    session.startListening,
  ]);

  // Autoplay recovery: Listen for one-time click/touch/pointer/key if browser autoplay policy blocks audio
  useEffect(() => {
    if (!session.isAutoplayBlocked) return;
    const handleFirstInteraction = () => {
      if (session.resumeAutoplay) {
        session.resumeAutoplay();
      }
    };
    window.addEventListener('pointerdown', handleFirstInteraction, { once: true });
    window.addEventListener('click', handleFirstInteraction, { once: true });
    window.addEventListener('touchstart', handleFirstInteraction, { once: true });
    window.addEventListener('keydown', handleFirstInteraction, { once: true });
    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [session.isAutoplayBlocked, session.resumeAutoplay]);

  // Safety timeout: ensure opening audio gate resolves quickly (1.2s max wait) to prevent loading freezes
  useEffect(() => {
    if (!isOpeningAudioReady) {
      const timer = setTimeout(() => {
        setIsOpeningAudioReady(true);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isOpeningAudioReady]);

  const rawUser: any = user || {};
  const learnerFromSession = (session.briefing as any)?.learnerName ||
    (session.briefing as any)?.user?.name ||
    (session as any)?.attempt?.learnerName ||
    (session as any)?.attempt?.user?.name;

  const rawName = rawUser.name ||
    (rawUser.firstName && rawUser.lastName ? `${rawUser.firstName} ${rawUser.lastName}` : null) ||
    rawUser.firstName ||
    learnerFromSession;

  const formatEmailName = (email?: string) => {
    if (!email) return 'Learner';
    const handle = email.split('@')[0];
    return handle
      .replace(/[._-]/g, ' ')
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  };

  const userName = (rawName && rawName.trim().length > 0) ? rawName.trim() : formatEmailName(user?.email);

  const nameParts = userName.split(/\s+/).filter(Boolean);
  const userInitials = nameParts.length > 1
    ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
    : (userName.slice(0, 2) || 'LE').toUpperCase();

  const stages: any[] = simData.stages || bData.stages || [
    { name: 'Introduction & Context', objective: 'Acknowledge and discover core needs.' }
  ];
  const latestStageTurn = useMemo(() => {
    return [...session.turns].reverse().find(t => typeof (t as any).stageIndex === 'number' && !isNaN((t as any).stageIndex));
  }, [session.turns]);
  const turnStageIdx = latestStageTurn ? Number((latestStageTurn as any).stageIndex) : undefined;
  const rawStageIdx = Math.max(session.currentStageIndex ?? 0, turnStageIdx ?? ((session.briefing as any)?.currentStageIndex ?? 0));
  const currentStageIdx = Math.min(stages.length - 1, Math.max(0, rawStageIdx));
  const currentStage = stages[currentStageIdx] || stages[0];
  const currentStageName = currentStage?.name || 'Stage 1';

  // Duration & Elapsed formatting
  const totalDurationSeconds = (simData.timeLimitMinutes || bData.timeLimitMinutes || simData.durationMinutes || 8) * 60;
  const elapsedSeconds = Math.max(0, totalDurationSeconds - session.secondsRemaining);

  const formatSecs = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const formattedElapsed = formatSecs(elapsedSeconds);
  const formattedTotal = formatSecs(totalDurationSeconds);
  const timerProgressPct = Math.max(0, Math.min(100, (session.secondsRemaining / totalDurationSeconds) * 100));

  // Language display
  const langParam = (props.language || urlLang || 'en').toLowerCase();
  const languageLabel = langParam.startsWith('hi')
    ? 'Hindi (India)'
    : langParam.startsWith('ta')
      ? 'Tamil (India)'
      : 'English (India)';

  // Find latest AI utterance text for floating caption & repeat
  const latestAiTurn = useMemo(() => {
    const aiTurns = session.turns.filter((t) => !['learner', 'user', 'me', 'student'].includes((t.speaker || (t as any).role || '').toLowerCase()));
    return aiTurns.length > 0 ? aiTurns[aiTurns.length - 1] : null;
  }, [session.turns]);

  // Real-time Learner speech: combines accumulated finalized speech and live interim words
  const activeLearnerSpeech = useMemo(() => {
    const raw = [session.pendingTranscript, session.interimTranscript].filter(Boolean).join(' ').trim();
    const clean = raw.replace(/^[.\s…]+|[.\s…]+$/g, '').trim();
    if (!clean || clean.startsWith('🎙️') || clean === '...' || clean === '..') {
      return '';
    }
    return raw;
  }, [session.pendingTranscript, session.interimTranscript]);

  // Auto-scroll mobile transcript drawer on update.
  // Smooth only for committed turns / drawer open; streaming speech scrolls instantly.
  useEffect(() => {
    if (isTranscriptDrawerOpen && transcriptDrawerEndRef.current) {
      try {
        transcriptDrawerEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch { }
    }
  }, [isTranscriptDrawerOpen, session.turns.length]);

  useEffect(() => {
    if (!isTranscriptDrawerOpen || !activeLearnerSpeech) return;
    if (drawerInterimScrollScheduledRef.current) return;
    drawerInterimScrollScheduledRef.current = true;
    requestAnimationFrame(() => {
      drawerInterimScrollScheduledRef.current = false;
      if (isTranscriptDrawerOpen && transcriptDrawerEndRef.current && document.body.contains(transcriptDrawerEndRef.current)) {
        try {
          transcriptDrawerEndRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        } catch { }
      }
    });
  }, [isTranscriptDrawerOpen, activeLearnerSpeech]);

  // Global Enter key listener for instant conversational turn submission
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Barge-In / Interruption via Spacebar when AI is speaking
      if ((e.key === ' ' || e.code === 'Space') && session.isAiSpeaking) {
        const target = e.target as HTMLElement;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
          return;
        }
        e.preventDefault();
        // Single path: stops TTS/audio, logs the interruption against the persona turn,
        // and reopens the microphone immediately so the interim transcript is captured.
        session.handleBargeIn();
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
          return; // Let input elements handle their own onKeyDown
        }
        if (activeLearnerSpeech && !session.isAiSpeaking && session.state !== 'processing') {
          e.preventDefault();
          session.handleInstantSubmit();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeLearnerSpeech, session.isAiSpeaking, session.state, session.handleInstantSubmit, session.handleBargeIn]);

  // Helper to accurately associate a turn with a specific persona
  const isTurnSpokenByPersona = useCallback(
    (turn: Turn | null, p: any, pIdx: number) => {
      if (!turn || ['learner', 'user', 'me', 'student'].includes((turn.speaker || (turn as any).role || '').toLowerCase())) return false;
      if (personasList.length <= 1) return true;

      const turnPersonaId = (turn as any).personaId;
      if (turnPersonaId && p.id) {
        return turnPersonaId === p.id;
      }

      const turnSpeaker = (turn as any).personaName || (turn as any).speakerName;
      if (turnSpeaker) {
        const cleanSpeaker = turnSpeaker.split('(')[0].trim().toLowerCase();
        const pName = (p.name || '').toLowerCase();
        if (pName === cleanSpeaker || pName.includes(cleanSpeaker) || cleanSpeaker.includes(pName)) {
          return true;
        }
      }

      // Default to primary Turn 1 persona if unassigned
      return pIdx === 0;
    },
    [personasList]
  );

  // If latest AI turn matches a specific persona name or id, sync active persona
  useEffect(() => {
    if (latestAiTurn) {
      const turnSpeaker = (latestAiTurn as any).personaName || (latestAiTurn as any).speakerName;
      const turnPersonaId = (latestAiTurn as any).personaId;
      if (turnPersonaId) {
        const foundIdx = personasList.findIndex((p) => p.id === turnPersonaId);
        if (foundIdx !== -1) {
          setActivePersonaIdx(foundIdx);
          return;
        }
      }
      if (turnSpeaker) {
        const cleanSpeaker = turnSpeaker.split('(')[0].trim().toLowerCase();
        const foundIdx = personasList.findIndex(
          (p) =>
            p.name.toLowerCase() === cleanSpeaker ||
            p.name.toLowerCase().includes(cleanSpeaker) ||
            cleanSpeaker.includes(p.name.toLowerCase())
        );
        if (foundIdx !== -1) {
          setActivePersonaIdx(foundIdx);
        }
      }
    }
  }, [latestAiTurn, personasList]);

  const handleRepeatAiTurn = useCallback(
    (turnText?: string, turnAudioUrl?: string, personaName?: string) => {
      if (isRepeatingTurnRef.current || session.isAudioLoading || session.isAiSpeaking) return;
      isRepeatingTurnRef.current = true;
      setTimeout(() => {
        isRepeatingTurnRef.current = false;
      }, 700);

      if (turnText) {
        const foundPersona = personasList.find(
          (p) => personaName && (p.name.toLowerCase() === personaName.toLowerCase() || p.id === personaName)
        ) || personasList[activePersonaIdx] || personasList[0];

        session.speakText(turnText, turnAudioUrl, undefined, {
          personaName: personaName || foundPersona?.name,
          ttsVoice: (latestAiTurn as any)?.ttsVoice || foundPersona?.ttsVoice,
          ttsProvider: (latestAiTurn as any)?.ttsProvider || foundPersona?.ttsProvider,
          country: (latestAiTurn as any)?.country || foundPersona?.country,
          tone: (latestAiTurn as any)?.tone || foundPersona?.tone,
          pitch: (latestAiTurn as any)?.pitchPreference || foundPersona?.pitchPreference,
          rate: (latestAiTurn as any)?.speakingPace || foundPersona?.speakingPace,
          genderPreference: (latestAiTurn as any)?.genderPreference || foundPersona?.genderPreference,
        });
      }
    },
    [session, personasList, activePersonaIdx, latestAiTurn]
  );

  const handleRepeatLatestAiTurn = () => {
    if (session.isAudioLoading || session.isAiSpeaking || isRepeatingTurnRef.current) return;
    if (latestAiTurn?.text) {
      handleRepeatAiTurn(latestAiTurn.text, latestAiTurn.audioUrl, latestAiTurn.personaName);
    }
  };

  // Leaving before the dialogue starts returns the learner to the briefing dossier,
  // where the attempt created on entry is offered as a resume. Dropping them on the
  // dashboard instead strands an active attempt they cannot see or continue.
  const handleExitToBriefing = useCallback(() => {
    const langParam = effectiveLanguage ? `?lang=${effectiveLanguage}` : '';
    if (targetScenarioId) {
      navigate(`/simulations/${targetScenarioId}/briefing${langParam}`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, targetScenarioId, effectiveLanguage]);

  const handleVoiceCheckRetry = async () => {
    if (isRetryingVoice) return;
    setIsRetryingVoice(true);
    try {
      const ok = await session.ensureMicrophone();
      setVoiceCheckState(ok ? 'ready' : 'blocked');
    } catch {
      setVoiceCheckState('blocked');
    } finally {
      setIsRetryingVoice(false);
    }
  };

  const handleEndSimulationConfirm = async () => {
    if (isEndingSession) return;
    setShowEndModal(false);
    setIsEndingSession(true);
    session.stopAll();
    stopAllSpeech();
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch { }
    }

    try {
      await Promise.race([
        faceAnalysis.finalizeAnalysis(),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    } catch (faErr) {
      console.warn('[SimulationRoom] Face analysis finalize error:', faErr);
    }

    try {
      await Promise.race([
        recorder.stopRecording(),
        new Promise((resolve) => setTimeout(resolve, 15000)),
      ]);
    } catch (recErr) {
      console.warn('[SimulationRoom] Recorder stop error:', recErr);
    }

    setIsCameraOn(false);
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    const effectiveAttemptId = session.attemptId || targetAttemptId;
    try {
      if (typeof session.endSimulation === 'function') {
        await Promise.race([
          session.endSimulation(),
          new Promise((resolve) => setTimeout(resolve, 25000)),
        ]);
      }
    } catch (endErr) {
      console.warn('[SimulationRoom] session.endSimulation error:', endErr);
    }

    if (props.onEndSimulation) {
      props.onEndSimulation(session.turns, effectiveAttemptId);
    } else {
      if (effectiveAttemptId) {
        navigate(`/attempts/${effectiveAttemptId}/results`);
      } else {
        navigate('/attempts');
      }
    }
  };

  if (session.error && session.turns.length === 0) {
    return (
      <div
        style={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'radial-gradient(ellipse at 50% 30%, #0A1E4A 0%, #050F26 70%)',
          fontFamily: 'var(--body, sans-serif)',
          color: '#FFFFFF',
          padding: '24px',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            maxWidth: '480px',
            width: '100%',
            background: 'rgba(15, 35, 80, 0.85)',
            border: '1px solid rgba(229, 72, 77, 0.4)',
            borderRadius: '20px',
            padding: '36px 32px',
            textAlign: 'center',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(229, 72, 77, 0.18)',
              border: '1px solid rgba(229, 72, 77, 0.4)',
              color: '#FF8A8E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px',
            }}
          >
            ⚠️
          </div>
          <h2
            style={{
              fontFamily: 'var(--display, Inter, sans-serif)',
              fontSize: '20px',
              fontWeight: 700,
              color: '#FFFFFF',
              margin: '0 0 10px',
              letterSpacing: '-0.02em',
            }}
          >
            {t('simulation.unable_to_start', undefined, 'Unable to Start Simulation')}
          </h2>
          <p
            style={{
              fontSize: '13.5px',
              color: '#9AABCB',
              margin: '0 0 24px',
              lineHeight: '1.5',
            }}
          >
            {session.error}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={async () => {
                if (typeof session.reconnect === 'function') {
                  await session.reconnect();
                } else {
                  window.location.reload();
                }
              }}
              disabled={session.isReconnecting}
              style={{
                background: 'linear-gradient(135deg, #0D4FBD, #2F7BFF)',
                color: '#FFFFFF',
                border: 0,
                borderRadius: '10px',
                padding: '10px 20px',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: session.isReconnecting ? 'wait' : 'pointer',
                boxShadow: '0 4px 14px rgba(47, 123, 255, 0.35)',
              }}
            >
              ⚡ {session.isReconnecting ? t('simulation.reconnecting', undefined, 'Reconnecting…') : t('common.retry', undefined, 'Retry')}
            </button>
            <button
              type="button"
              onClick={handleExitToBriefing}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#CBD5E1',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                padding: '10px 20px',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← {t('simulation.back_to_briefing', undefined, 'Back to briefing')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#CBD5E1',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                padding: '10px 20px',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('nav.dashboard', undefined, 'Dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Startup readiness signals — surfaced on the loading screen so the learner always
  // knows what the room is still waiting on instead of watching a decorative spinner.
  const micPhaseDone = Boolean(session.micReady || session.isListening) || voiceCheckState === 'ready';
  const micPhaseStatus = micPhaseDone
    ? 'Ready'
    : voiceCheckState === 'blocked'
      ? 'Action needed'
      : voiceCheckState === 'checking'
        ? 'Checking…'
        : 'Waiting';
  const dialogueEngineStatus = dialogueEngineDone
    ? 'Ready'
    : session.isReconnecting
      ? 'Reconnecting…'
      : (session as any).initProgressStage === 'starting' || session.state === 'starting'
        ? 'Starting…'
        : session.error
          ? 'Action needed'
          : 'Connecting…';
  const audioAssetStatus = audioAssetPhaseDone
    ? 'Ready'
    : 'Buffering…';
  const roomInitPhases = [
    { label: t('simulation.init_briefing_loaded', undefined, 'Scenario briefing & persona directives'), done: Boolean(session.briefing), status: session.briefing ? 'Ready' : undefined },
    { label: t('simulation.init_dialogue_connected', undefined, 'Dialogue engine connected'), done: dialogueEngineDone, status: dialogueEngineStatus },
    { label: t('simulation.init_mic_ready', undefined, 'Microphone ready for barge-in'), done: micPhaseDone, status: micPhaseStatus },
    { label: t('simulation.init_audio_loaded', undefined, 'Required audio assets loaded & decoded'), done: audioAssetPhaseDone, status: audioAssetStatus },
  ];
  const roomInitDone = roomInitPhases.filter((p) => p.done).length;
  const roomInitProgress = Math.min(95, 15 + Math.round((roomInitDone / roomInitPhases.length) * 80));

  const isRoomInitializing = (session.isLoadingScenario || !audioAssetPhaseDone) && !showPreflightModal;
  if (isRoomInitializing) {
    return (
      <div
        style={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'radial-gradient(ellipse at 50% 30%, #0A1E4A 0%, #050F26 70%)',
          fontFamily: 'var(--body, sans-serif)',
          color: '#FFFFFF',
          padding: '24px',
          boxSizing: 'border-box',
          position: 'relative',
          overflowX: 'hidden',
        }}
      >
        <style>{`
          @keyframes trainovaSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes trainovaPulse {
            0%, 100% { opacity: 0.6; transform: scale(0.98); }
            50% { opacity: 1; transform: scale(1.02); }
          }
        `}</style>

        <button
          type="button"
          onClick={handleExitToBriefing}
          aria-label={t('simulation.exit_to_briefing', undefined, 'Exit to scenario briefing')}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255, 255, 255, 0.06)',
            color: '#CBD5E1',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            borderRadius: '999px',
            padding: '8px 14px',
            fontSize: '12.5px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true">←</span> {t('simulation.exit_to_briefing', undefined, 'Exit to briefing')}
        </button>

        <div
          style={{
            position: 'relative',
            width: '90px',
            height: '90px',
            marginBottom: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Glowing orbital ring */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '3px solid rgba(47, 123, 255, 0.15)',
              borderTopColor: '#2F7BFF',
              borderRightColor: '#60A5FA',
              animation: 'trainovaSpin 1s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite',
              boxShadow: '0 0 30px rgba(47, 123, 255, 0.35)',
            }}
          />
          {/* Inner badge */}
          <div
            style={{
              width: '54px',
              height: '54px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0D4FBD, #0B2A5E)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: '22px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              animation: 'trainovaPulse 2s ease-in-out infinite',
            }}
          >
            🎙️
          </div>
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px',
            borderRadius: '999px',
            background: 'rgba(47, 123, 255, 0.15)',
            border: '1px solid rgba(47, 123, 255, 0.3)',
            marginBottom: '16px',
          }}
        >
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#38BDF8', display: 'inline-block' }} />
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#7FB0FF',
            }}
          >
            Trainova AI Simulation Room
          </span>
        </div>

        <h2
          style={{
            fontFamily: 'var(--display, Inter, sans-serif)',
            fontSize: '22px',
            fontWeight: 700,
            color: '#FFFFFF',
            margin: '0 0 8px',
            letterSpacing: '-0.02em',
            textAlign: 'center',
          }}
        >
          {t('simulation.initializing_room_title', undefined, 'Initializing Simulation Room...')}
        </h2>

        <p
          style={{
            fontSize: '13.5px',
            color: '#9AABCB',
            margin: '0 0 24px',
            textAlign: 'center',
            maxWidth: '460px',
            lineHeight: '1.5',
          }}
        >
          {initTimeoutElapsed
            ? t('simulation.init_slow_desc', undefined, 'Connecting to the AI roleplay engine is taking longer than usual. You can wait a moment or trigger a reconnect.')
            : t('simulation.init_loading_desc', undefined, 'Loading scenario briefing, persona directives & starting dialogue.')}
        </p>

        {/* Readiness stepper */}
        <div
          style={{
            width: 'min(320px, 86vw)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '18px',
          }}
        >
          {roomInitPhases.map((phase, idx) => (
            <div key={phase.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: phase.done ? '#06130B' : '#7FB0FF',
                  background: phase.done ? '#34D399' : 'rgba(47, 123, 255, 0.12)',
                  border: `1px solid ${phase.done ? '#34D399' : 'rgba(47, 123, 255, 0.3)'}`,
                  transition: 'background 0.35s ease, border-color 0.35s ease, color 0.35s ease',
                }}
              >
                {phase.done ? '✓' : idx + 1}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '12.5px',
                  fontWeight: phase.done ? 600 : 500,
                  color: phase.done ? '#8BE6B0' : '#9AABCB',
                  transition: 'color 0.35s ease',
                  textAlign: 'left',
                }}
              >
                {phase.label}
              </span>
              <span style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: phase.done ? '#34D399' : phase.status === 'Action needed' ? '#F87171' : '#5C6E90' }}>
                {phase.status || (phase.done ? t('simulation.init_ready', undefined, 'Ready') : t('simulation.init_waiting', undefined, 'Waiting'))}
              </span>
            </div>
          ))}
        </div>

        {voiceCheckState === 'blocked' && (
          <div
            role="alert"
            style={{
              width: 'min(320px, 86vw)',
              background: 'rgba(229, 72, 77, 0.12)',
              border: '1px solid rgba(229, 72, 77, 0.4)',
              borderRadius: '10px',
              padding: '12px 14px',
              marginTop: '18px',
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#FFB4B6', marginBottom: '4px' }}>
              {t('simulation.mic_blocked_init_title', undefined, 'Microphone access is blocked')}
            </div>
            <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#F3C6C7', lineHeight: 1.5 }}>
              {t('simulation.mic_blocked_init_desc', undefined, "Allow microphone access in your browser's address bar, then retry. If no prompt appears, this browser or connection may not support microphone capture — the room can still open with typed replies.")}
            </p>
            <button
              type="button"
              onClick={handleVoiceCheckRetry}
              disabled={isRetryingVoice}
              style={{
                background: isRetryingVoice ? 'rgba(255, 255, 255, 0.08)' : '#0D4FBD',
                color: '#FFFFFF',
                border: 0,
                borderRadius: '8px',
                padding: '7px 14px',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: isRetryingVoice ? 'wait' : 'pointer',
              }}
            >
              {isRetryingVoice ? t('simulation.requesting_access', undefined, 'Requesting access…') : t('simulation.retry_mic_init', undefined, 'Retry microphone')}
            </button>
          </div>
        )}

        {voiceCheckState === 'checking' && (
          <p style={{ fontSize: '12px', color: '#7FB0FF', margin: '14px 0 0', textAlign: 'center' }}>
            {t('simulation.mic_checking_desc', undefined, 'Allow microphone access if your browser asks — you can speak the moment the room opens.')}
          </p>
        )}

        {/* Progress bar driven by the readiness signals above */}
        <div
          style={{
            width: 'min(320px, 86vw)',
            height: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '999px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${roomInitProgress}%`,
              background: 'linear-gradient(90deg, #0D4FBD, #38BDF8)',
              borderRadius: '999px',
              transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '24px' }}>
          {initTimeoutElapsed && (
            <button
              type="button"
              onClick={async () => {
                if (typeof session.reconnect === 'function') {
                  setInitTimeoutElapsed(false);
                  await session.reconnect();
                } else {
                  window.location.reload();
                }
              }}
              disabled={session.isReconnecting}
              style={{
                background: 'linear-gradient(135deg, #0D4FBD, #2F7BFF)',
                color: '#FFFFFF',
                border: 0,
                borderRadius: '8px',
                padding: '8px 18px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: session.isReconnecting ? 'wait' : 'pointer',
                boxShadow: '0 4px 12px rgba(47, 123, 255, 0.35)',
              }}
            >
              🔄 {session.isReconnecting ? t('simulation.reconnecting', undefined, 'Reconnecting…') : t('simulation.reconnect_room', undefined, 'Reconnect Room')}
            </button>
          )}
          <button
            type="button"
            onClick={handleExitToBriefing}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#CBD5E1',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ← {t('simulation.back_to_briefing', undefined, 'Back to briefing')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#CBD5E1',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('nav.dashboard', undefined, 'Dashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="view-section active lv-sim-fullscreen" id="viewLiveSimulation">
      <div className="stage livecall" id="live-stage">
        <div className="lv-screen">

          {/* ================================================================ */}
          {/* Top Bar (.lv-top) */}
          {/* ================================================================ */}
          <header className="lv-top">
            <div className="lv-brand" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 200px', minWidth: '140px', overflow: 'hidden' }}>
              <div style={{ fontFamily: 'var(--display, Inter, sans-serif)', fontSize: '18px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.01em', whiteSpace: 'nowrap', flexShrink: 0 }}>
                Trainova<span style={{ color: '#7FB0FF' }}>.ai</span>
              </div>
              <span className="lv-sep" style={{ flexShrink: 0 }} />
              <div className="lv-ttl" style={{ minWidth: 0, overflow: 'hidden' }}>
                <b id="liveSimTitle" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: 'min(58vw, 260px)' }} title={scenarioTitle}>
                  {scenarioTitle}
                </b>
                <span id="liveSimRole" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {category ? t('simulation.attempt_progress', { category, current: attemptNum, max: maxAttempts }, `${category} · Attempt ${attemptNum} of ${maxAttempts}`) : t('scorecard.attempt_counter', { current: attemptNum, max: maxAttempts }, `Attempt ${attemptNum} of ${maxAttempts}`)}
                </span>
              </div>
            </div>

            <div className="lv-mid" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span className="lv-rec" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <i style={{ background: recorder.recordingState === 'RECORDING' ? '#ef4444' : recorder.recordingState === 'FAILED' ? '#94a3b8' : '#f59e0b' }} />
                  {recorder.recordingState === 'RECORDING' ? t('simulation.rec_badge', undefined, 'REC') : recorder.recordingState === 'STARTING' ? t('simulation.init_badge', undefined, 'INIT') : recorder.recordingState === 'FAILED' ? t('simulation.nocam_badge', undefined, 'NO-CAM') : t('simulation.rec_badge', undefined, 'REC')}
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span>🎙️ {session.micPermissionDenied ? t('simulation.mic_muted', undefined, 'Blocked') : session.isMuted ? t('simulation.mic_muted', undefined, 'Muted') : t('simulation.mic_live', undefined, 'Live')}</span>
                  <span>📷 {isCameraOn ? t('simulation.camera_live', undefined, 'Live') : t('simulation.camera_off', undefined, 'Off')}</span>
                  {recorder.health.healthWarning && isCameraOn && (
                    <span style={{ color: '#fbbf24', fontSize: '10.5px' }}>⚠️ {recorder.health.healthWarning}</span>
                  )}
                </span>
              </div>

              <div className="lv-timer" id="liveSimTimerContainer">
                <div className="lv-ring" id="timerRingCircle" style={{ '--p': timerProgressPct } as React.CSSProperties}>
                  <span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                  </span>
                </div>
                <div>
                  <small>{t('simulation.time_left', undefined, 'Time left')}</small>
                  <b id="simTimerDisplay">{session.formattedTime}</b>
                </div>
                <div className="lv-el">
                  <small>{t('simulation.elapsed', undefined, 'Elapsed')}</small>
                  <span id="simTimerElapsed">{formattedElapsed} / {formattedTotal}</span>
                </div>
              </div>

              <div className="lv-stages" id="liveSimStages">
                <div className="lv-lbl" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{t('simulation.stage_label', undefined, 'Stage')}</span>
                    <b id="liveSimStageName">{t('simulation.stage_progress', { current: currentStageIdx + 1, total: stages.length, name: currentStageName }, `${currentStageIdx + 1} of ${stages.length} · ${currentStageName}`)}</b>
                  </div>
                  <span
                    className="lv-done-counter"
                    id="liveSimDoneCount"
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: 'var(--blue-2, #38BDF8)',
                      background: 'rgba(56, 189, 248, 0.12)',
                      border: '1px solid rgba(56, 189, 248, 0.28)',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      letterSpacing: '0.02em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Done: {currentStageIdx} of {stages.length}
                  </span>
                </div>
                <div className="lv-segs" id="liveSimSegs">
                  {stages.map((_, idx) => (
                    <i key={idx} className={idx < currentStageIdx ? 'lv-d' : idx === currentStageIdx ? 'lv-c' : ''} />
                  ))}
                </div>
              </div>
            </div>

            <div className="lv-right" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <button
                type="button"
                className="btn sm"
                id="liveInspectPromptsBtn"
                onClick={() => setShowTraceModal(true)}
                style={{
                  background: 'rgba(59, 130, 246, 0.22)',
                  border: '1px solid rgba(59, 130, 246, 0.45)',
                  color: '#93c5fd',
                  borderRadius: '8px',
                  padding: '5px 10px',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
                title={t('simulation.inspect_trace_tooltip', undefined, 'Inspect real-time LLM API calls, system prompts, and dialogue states')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '13px', height: '13px' }}>
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <span>🔍 {t('simulation.live_llm_trace', undefined, 'Live LLM Trace')}</span>
              </button>

              <span className="lv-chip" id="liveSimLangChip" style={{ whiteSpace: 'nowrap' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                </svg>
                <span id="liveSimLangLabel">{languageLabel}</span>
              </span>
            </div>
          </header>

          {/* ================================================================ */}
          {/* Main Body: Video Stage + Scenario & Transcript Side Panel */}
          {/* ================================================================ */}
          <div className="lv-body lv-body-responsive" style={{ minHeight: 0, height: '100%', overflowY: 'auto' }}>
            <div className="lv-stage" style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

              {/* Multi-Persona In-Call Indicator Bar */}
              {personasList.length > 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 14px',
                    background: 'rgba(5, 15, 38, 0.95)',
                    borderBottom: '1px solid var(--line)',
                    flexShrink: 0,
                    overflowX: 'auto',
                  }}
                >
                  <span style={{ fontSize: '11px', color: '#9AABCB', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                    Participants ({personasList.length}):
                  </span>
                  {personasList.map((p, idx) => {
                    const isActive = activePersonaIdx === idx;
                    const isSpeakingNow = session.isAiSpeaking && isActive;

                    return (
                      <button
                        key={p.id || idx}
                        type="button"
                        onClick={() => setActivePersonaIdx(idx)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '3px 10px',
                          borderRadius: '999px',
                          fontSize: '11.5px',
                          fontWeight: isActive ? 700 : 500,
                          background: isActive ? 'rgba(47, 123, 255, 0.22)' : 'rgba(255, 255, 255, 0.05)',
                          border: isActive ? '1px solid #2F7BFF' : '1px solid rgba(255, 255, 255, 0.12)',
                          color: isActive ? '#FFFFFF' : '#9AABCB',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease-in-out',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            background: isSpeakingNow ? '#35D07F' : isActive ? '#38BDF8' : '#6B7280',
                            boxShadow: isSpeakingNow ? '0 0 8px #35D07F' : 'none',
                          }}
                        />
                        <span>{p.name}</span>
                        <span style={{ opacity: 0.65, fontSize: '10.5px' }}>({p.role})</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Autoplay Restriction Interaction Banner */}
              {session.isAutoplayBlocked && (
                <div
                  id="autoplayBlockedBanner"
                  onClick={session.resumeAutoplay}
                  style={{
                    background: 'linear-gradient(90deg, #0D4FBD, #071D45)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    color: '#FFFFFF',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    zIndex: 25,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(13, 79, 189, 0.35)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: '18px', flexShrink: 0 }}>🔊</span>
                    <span>
                      {t(
                        'simulation.autoplay_blocked_banner',
                        undefined,
                        'Click anywhere or tap Play Voice to enable AI persona audio'
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    id="resumeAutoplayBtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (session.resumeAutoplay) {
                        session.resumeAutoplay();
                      }
                    }}
                    style={{
                      background: '#FFFFFF',
                      color: '#071D45',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '5px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    ▶ {t('simulation.play_audio_btn', undefined, 'Play Voice')}
                  </button>
                </div>
              )}

              {/* Browser Microphone Permission Alert Banner */}
              {session.micPermissionDenied && !dismissedMicBanner && (
                <div
                  id="micPermissionBlockedBanner"
                  style={{
                    background: 'linear-gradient(90deg, rgba(220, 38, 38, 0.95), rgba(185, 28, 28, 0.95))',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    color: '#FFFFFF',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    zIndex: 20,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                    <div>
                      <div>
                        {t(
                          'simulation.mic_blocked_banner',
                          undefined,
                          'Microphone access is blocked by your browser. Click the lock/settings icon in your address bar to allow microphone access, or switch to Text Mode to continue.'
                        )}
                      </div>
                      {showMicHelp && (
                        <div style={{ marginTop: '5px', fontSize: '11.5px', color: '#FEF08A', fontWeight: 500, lineHeight: 1.4 }}>
                          ℹ️ {t(
                            'simulation.mic_blocked_help',
                            undefined,
                            'To allow: Click the 🔒 or 🎛️ icon next to the URL in your browser bar, set Microphone to "Allow", and click Retry Mic.'
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      id="howToAllowMicBtn"
                      onClick={() => setShowMicHelp((prev) => !prev)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        color: '#FFFFFF',
                        border: '1px solid rgba(255, 255, 255, 0.35)',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      title={t('simulation.mic_permission_help_title', undefined, 'Microphone permission help')}
                    >
                      {showMicHelp ? '✕' : t('simulation.mic_help_btn', undefined, 'ℹ️ Help')}
                    </button>
                    <button
                      type="button"
                      id="retryMicAccessBtn"
                      onClick={async () => {
                        if (session.retryMicAccess) {
                          const ok = await session.retryMicAccess();
                          if (ok) {
                            setDismissedMicBanner(false);
                            setShowMicHelp(false);
                          } else {
                            setShowMicHelp(true);
                          }
                        }
                      }}
                      style={{
                        background: '#FFFFFF',
                        color: '#B91C1C',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                      }}
                    >
                      ↻ {t('simulation.retry_mic', undefined, 'Retry Mic')}
                    </button>
                    <button
                      type="button"
                      id="useTextModeBtn"
                      onClick={() => {
                        setDismissedMicBanner(true);
                        setShowManualTextInput(true);
                        setTimeout(() => {
                          const inputEl = document.getElementById('manualTurnInput');
                          if (inputEl) {
                            inputEl.focus();
                          }
                        }, 60);
                      }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: '#FFFFFF',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      ⌨️ {t('simulation.use_text_mode', undefined, 'Use Text Mode')}
                    </button>
                    <button
                      type="button"
                      id="dismissMicBannerBtn"
                      aria-label={t('simulation.dismiss_banner', undefined, 'Dismiss banner')}
                      onClick={() => setDismissedMicBanner(true)}
                      style={{
                        background: 'transparent',
                        color: '#FFFFFF',
                        border: 'none',
                        fontSize: '16px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        padding: '4px 6px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* Audio Loading Error Alert Banner */}
              {session.audioLoadingError && !dismissedAudioError && (
                <div
                  id="audioLoadingErrorBanner"
                  style={{
                    background: 'linear-gradient(90deg, rgba(178, 106, 0, 0.95), rgba(154, 90, 0, 0.95))',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    color: '#FFFFFF',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    zIndex: 20,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: '15px' }}>⚠️</span>
                    <span>
                      {session.audioLoadingError}. {t('simulation.audio_error_desc', undefined, 'Voice synthesis will be used if available, or you can retry playback below.')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {session.turns.length > 0 && (
                      <button
                        type="button"
                        id="retryAudioLoadBtn"
                        onClick={() => {
                          setDismissedAudioError(false);
                          handleRepeatLatestAiTurn();
                        }}
                        disabled={session.isAudioLoading || session.isAiSpeaking}
                        style={{
                          background: '#FFFFFF',
                          color: '#B26A00',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: (session.isAudioLoading || session.isAiSpeaking) ? 'not-allowed' : 'pointer',
                          opacity: (session.isAudioLoading || session.isAiSpeaking) ? 0.6 : 1,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        }}
                      >
                        {t('simulation.retry_audio', undefined, 'Retry Audio')}
                      </button>
                    )}
                    <button
                      type="button"
                      id="dismissAudioErrorBtn"
                      aria-label={t('simulation.dismiss_banner', undefined, 'Dismiss audio error')}
                      onClick={() => setDismissedAudioError(true)}
                      style={{
                        background: 'transparent',
                        color: '#FFFFFF',
                        border: 'none',
                        fontSize: '16px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        padding: '4px 6px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* Video Grid Stage */}
              <div
                className={`lv-tiles ${personasList.length === 1 ? 'lv-one' : personasList.length === 2 ? 'lv-two' : 'lv-multi'}`}
                id="liveTilesContainer"
                style={
                  personasList.length > 2
                    ? {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: '12px',
                      flex: 1,
                      minHeight: 0,
                      padding: '12px',
                      overflowY: 'auto',
                    }
                    : { flex: 1, minHeight: 0 }
                }
              >
                {/* Render Each AI Persona Tile */}
                {personasList.map((p, pIdx) => {
                  const isThisPersonaTurn = isTurnSpokenByPersona(latestAiTurn, p, pIdx);
                  const isCurrentSpeaking = session.isAiSpeaking && isThisPersonaTurn;

                  return (
                    <div
                      key={p.id || pIdx}
                      className={`lv-tile lv-ai ${isCurrentSpeaking ? 'lv-speaking' : ''} ${personasList.length === 2 ? (pIdx === 0 ? 'lv-a1' : 'lv-a2') : ''
                        }`}
                      id={`aiVideoTile-${pIdx}`}
                      onClick={() => setActivePersonaIdx(pIdx)}
                      style={{ cursor: personasList.length > 1 ? 'pointer' : 'default' }}
                    >
                      <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                        <SpeechControlledAvatar
                          avatarUrl={p.avatarUrl}
                          avatarType={p.avatarType}
                          isSpeaking={isCurrentSpeaking}
                          personaName={p.name}
                          genderPreference={p.genderPreference}
                          className="lv-av-img"
                          renderFallback={() => (
                            <div
                              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                              dangerouslySetInnerHTML={{ __html: getPersonaAvatarSVG(p.name) }}
                            />
                          )}
                        />
                      </div>

                      {isCurrentSpeaking && (
                        <span className="lv-spk" id={`aiSpeakingBadge-${pIdx}`}>{t('simulation.speaking_badge', undefined, 'Speaking')}</span>
                      )}
                      {!isCurrentSpeaking && session.isAudioLoading && isThisPersonaTurn && (
                        <span
                          className="lv-spk"
                          id={`aiLoadingBadge-${pIdx}`}
                          style={{
                            background: '#0D4FBD',
                            color: '#FFFFFF',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            boxShadow: '0 2px 8px rgba(13, 79, 189, 0.4)',
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              border: '1.5px solid #93C5FD',
                              borderTopColor: 'transparent',
                              animation: 'spin 0.8s linear infinite',
                              display: 'inline-block',
                            }}
                          />
                          {t('simulation.buffering_audio', undefined, 'Buffering audio…')}
                        </span>
                      )}
                      {!isCurrentSpeaking && !session.isAudioLoading && session.state === 'processing' && isThisPersonaTurn && (
                        <span className="lv-spk" id={`aiThinkingBadge-${pIdx}`} style={{ background: '#2563EB', color: '#FFFFFF' }}>
                          {t('simulation.persona_thinking', undefined, 'Thinking...')}
                        </span>
                      )}

                      <div className="lv-tag">
                        <span className="lv-aib">{t('simulation.role_ai', undefined, 'AI')}</span>
                        <b style={{ whiteSpace: 'nowrap', flexShrink: 0, color: '#fff', fontSize: '13px' }}>{p.name}</b>
                        <span className="lv-role" title={p.role} style={{ flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{translateRole(p.role, t)}</span>
                        {p.mood && p.mood !== 'Neutral' && (
                          <span
                            className="lv-mood"
                            style={{
                              marginLeft: '4px',
                              fontSize: '10px',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              background: 'rgba(255,255,255,0.12)',
                              color: '#FDE047',
                              flexShrink: 0,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {translateMood(p.mood, t)}
                          </span>
                        )}
                        <span
                          className="lv-ai-voice-chip"
                          style={{
                            marginLeft: '6px',
                            fontSize: '9px',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            background: 'rgba(255, 255, 255, 0.08)',
                            color: 'rgba(255, 255, 255, 0.7)',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                          title={t('simulation.ai_voice_tooltip', undefined, "This persona's voice is AI-generated")}
                        >
                          {t('simulation.ai_voice_chip', undefined, 'AI Voice')}
                        </span>
                      </div>

                      {isCurrentSpeaking && (
                        <div className="lv-wave">
                          <i style={{ height: '6px' }} /><i style={{ height: '12px' }} /><i style={{ height: '18px' }} /><i style={{ height: '10px' }} /><i style={{ height: '15px' }} /><i style={{ height: '7px' }} />
                        </div>
                      )}
                      {!isCurrentSpeaking && session.isAudioLoading && isThisPersonaTurn && (
                        <div
                          className="lv-audio-loading-pulse"
                          style={{
                            position: 'absolute',
                            bottom: '12px',
                            right: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(7, 29, 69, 0.75)',
                            border: '1px solid rgba(47, 123, 255, 0.5)',
                            borderRadius: '16px',
                            padding: '3px 8px',
                            fontSize: '10.5px',
                            color: '#BFDBFE',
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: '#38BDF8',
                              animation: 'pulse 1.2s infinite',
                            }}
                          />
                          <span>{t('simulation.buffering_audio', undefined, 'Buffering audio…')}</span>
                        </div>
                      )}
                      {!isCurrentSpeaking && !session.isAudioLoading && session.state === 'processing' && isThisPersonaTurn && (
                        <div className="lv-wave" style={{ opacity: 0.9 }}>
                          <i style={{ height: '6px', background: '#38BDF8' }} />
                          <i style={{ height: '12px', background: '#38BDF8' }} />
                          <i style={{ height: '18px', background: '#38BDF8' }} />
                          <i style={{ height: '12px', background: '#38BDF8' }} />
                          <i style={{ height: '6px', background: '#38BDF8' }} />
                        </div>
                      )}

                      <button
                        type="button"
                        className="lv-rep-btn"
                        id={`repeatAiTurnBtn-${pIdx}`}
                        title={session.isAudioLoading ? 'Audio is loading...' : session.isAiSpeaking ? 'Audio is playing...' : t('simulation.repeat_persona_tooltip', { name: p.name }, `Repeat ${p.name}'s latest response`)}
                        disabled={session.isAudioLoading || session.isAiSpeaking}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (session.isAudioLoading || session.isAiSpeaking) return;
                          const turns = session.turns.filter((t) => isTurnSpokenByPersona(t, p, pIdx));
                          const turnToRepeat = turns.length > 0 ? turns[turns.length - 1] : latestAiTurn;
                          if (turnToRepeat?.text) {
                            handleRepeatAiTurn(turnToRepeat.text, turnToRepeat.audioUrl, turnToRepeat.personaName || p.name);
                          }
                        }}
                        style={{
                          opacity: (session.isAudioLoading || session.isAiSpeaking) ? 0.45 : 1,
                          cursor: (session.isAudioLoading || session.isAiSpeaking) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12a8 8 0 1 0 2.4-5.7" />
                          <path d="M4 4v4h4" />
                          <path d="m10.5 9.5 4 2.5-4 2.5z" fill="currentColor" />
                        </svg>
                        {t('simulation.repeat', undefined, 'Repeat')}
                      </button>
                    </div>
                  );
                })}

                {/* Learner Video Tile */}
                <div className={`lv-tile lv-you ${isCameraOn ? 'lv-camera-active' : ''} ${personasList.length === 2 ? 'lv-a3' : ''} ${session.isListening && !session.isMuted && session.audioLevel > 15 ? 'lv-speaking' : ''}`} id="learnerVideoTile">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: isCameraOn ? 'block' : 'none' }}
                  />

                  {!isCameraOn && (
                    <span className="lv-phl" id="cameraDeniedOverlay" style={{ display: 'flex' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="6" width="13" height="12" rx="2" />
                        <path d="m16 10 5-3v10l-5-3" />
                      </svg>
                      {t('simulation.camera_off_overlay', undefined, 'Camera off')}
                    </span>
                  )}

                  {session.silenceCountdownSeconds !== null && session.silenceCountdownSeconds > 0 ? (
                    <span
                      className="lv-spk lv-countdown-badge"
                      id="learnerCountdownBadge"
                      style={{
                        background: 'rgba(15, 138, 95, 0.55)',
                        color: '#D6FBE6',
                        border: '1px solid rgba(53, 208, 127, 0.45)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontWeight: 700,
                      }}
                    >
                      ⏱️ {session.silenceCountdownSeconds}s
                    </span>
                  ) : session.isListening && !session.isMuted && session.audioLevel > 15 ? (
                    <span className="lv-spk" id="learnerSpeakingBadge">{t('simulation.speaking_badge', undefined, 'Speaking')}</span>
                  ) : null}

                  <div className="lv-tag lv-learner-tag">
                    <b>{t('simulation.you_label', undefined, 'You')}</b>
                    <span id="learnerTileName" className="lv-learner-tile-name">{userName}</span>
                  </div>

                  {session.isListening && !session.isMuted && session.audioLevel > 15 && (
                    <div className="lv-wave lv-g" id="learnerSpeakingWave">
                      <i style={{ height: '6px' }} /><i style={{ height: '13px' }} /><i style={{ height: '18px' }} /><i style={{ height: '10px' }} /><i style={{ height: '15px' }} /><i style={{ height: '7px' }} />
                    </div>
                  )}

                  {/* Local Face Analysis Real-time Status Overlay */}
                  {session.videoAnalysisEnabled && isCameraOn && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        background: 'rgba(7, 29, 69, 0.78)',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        fontSize: '11px',
                        color: '#E8F0FE',
                        zIndex: 5,
                        pointerEvents: 'none',
                      }}
                      title={t('simulation.privacy_video_notice', undefined, 'Observations are processed 100% locally in your browser. Raw video is never sent to the server.')}
                    >
                      <span
                        style={{
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          backgroundColor:
                            faceAnalysis.status === 'INITIALIZING'
                              ? '#FFB547'
                              : faceAnalysis.faceDetected
                                ? '#0F8A5F'
                                : '#F25555',
                        }}
                      />
                      <span>
                        {faceAnalysis.status === 'INITIALIZING'
                          ? 'Loading Face Model...'
                          : faceAnalysis.faceDetected
                            ? `Face In Frame (${faceAnalysis.currentConfidence || 0}%)${faceAnalysis.faceCentered ? ' • Centered' : ''}${faceAnalysis.approximateExpression
                              ? ` • ${faceAnalysis.approximateExpression}`
                              : ''
                            }`
                            : 'No Face In Frame'}
                      </span>
                      <span style={{ fontSize: '9px', opacity: 0.7, marginLeft: '2px' }}>(Local)</span>
                    </div>
                  )}

                </div>

                {/* Auto-Submit Countdown Floating Bar on the Video Stage */}
                {session.silenceCountdownSeconds !== null && session.silenceCountdownSeconds > 0 && activeLearnerSpeech && (
                  <div
                    id="autoSubmitCountdownBar"
                    className="lv-auto-submit-countdown-bar"
                    style={{
                      position: 'absolute',
                      bottom: '24px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 35,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 16px',
                      background: 'rgba(7, 29, 69, 0.94)',
                      backdropFilter: 'blur(12px)',
                      border: '1.5px solid rgba(47, 123, 255, 0.5)',
                      borderRadius: '30px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45), 0 0 16px rgba(47, 123, 255, 0.3)',
                      maxWidth: 'calc(100% - 32px)',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <div
                        id="autoSubmitCountdownNumber"
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #0D4FBD, #2F7BFF)',
                          color: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '13px',
                          boxShadow: '0 0 10px rgba(47, 123, 255, 0.5)',
                          flexShrink: 0,
                        }}
                      >
                        {session.silenceCountdownSeconds}
                      </div>
                      <div className="lv-countdown-text-col" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span className="lv-countdown-title" style={{ fontSize: '12.5px', fontWeight: 600, color: '#E2E8F0', whiteSpace: 'nowrap' }}>
                          {t('simulation.auto_submitting_in', { seconds: session.silenceCountdownSeconds }, `Auto-submitting in ${session.silenceCountdownSeconds}s…`)}
                        </span>
                        <span className="lv-countdown-subtitle" style={{ fontSize: '11px', color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t('simulation.speak_to_continue', undefined, 'Speak to continue or submit now')}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      id="submitResponseBtn"
                      onClick={session.handleInstantSubmit}
                      style={{
                        background: 'linear-gradient(135deg, #0F8A5F, #10B981)',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '8px 18px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 10px rgba(16, 185, 129, 0.35)',
                        whiteSpace: 'nowrap',
                        minHeight: '44px',
                        flexShrink: 0,
                      }}
                    >
                      <span>{t('simulation.submit_response', undefined, 'Submit Response')}</span>
                      <svg style={{ width: '13px', height: '13px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Side Panel: Scenario Guidance & Live Transcript */}
            {/* Sizing lives in CSS only: the inline flex/height/minHeight overrides used
                to beat the ≤900px rules and collapsed the transcript strip to ~24px. */}
            <aside
              className="lv-side"
              style={{
                borderLeft: '1px solid var(--line)',
                background: 'var(--bg2, #0A1A3D)',
              }}
            >
              {/* Collapsible Scenario Case */}
              <section className={`lv-case ${isScenarioOpen ? '' : 'lv-collapsed'}`} id="scenarioCaseDrawer" style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  className="lv-case-h"
                  id="toggleScenarioGuidanceBtn"
                  onClick={() => setIsScenarioOpen(!isScenarioOpen)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z" />
                    <path d="M14 3v5h5M9 13h6M9 17h6" />
                  </svg>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <b style={{ fontSize: '13.5px', color: '#fff' }}>{t('simulation.scenario_heading', undefined, 'Scenario')}</b>
                    <small id="scenarioSummaryText" style={{ display: 'block', fontSize: '11.5px', color: '#9AABCB', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isScenarioOpen ? t('simulation.scenario_hide_tip', undefined, 'Click to hide briefing') : (simData.title || bData.title || t('simulation.scenario_view_tip', undefined, 'Click to view full scenario briefing'))}
                    </small>
                  </span>
                  <span className="lv-tgl" id="scenarioToggleLabel">
                    {isScenarioOpen ? t('simulation.hide', undefined, 'Hide') : t('simulation.show', undefined, 'Show')}{' '}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transform: isScenarioOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                    >
                      <path d="m9 6 6 6-6 6" />
                    </svg>
                  </span>
                </button>
                {isScenarioOpen && (
                  <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '10px 12px' }}>
                      <b style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#FFFFFF', marginBottom: '4px' }}>
                        {simData.title || bData.title || 'Scenario Briefing'}
                      </b>
                      <p className="lv-case-p" id="scenarioFullDesc" style={{ margin: 0, fontSize: '12px', color: '#CBD5E1', lineHeight: '1.5' }}>
                        {simData.description || bData.description || 'Conduct the conversation professionally to achieve the learning objectives.'}
                      </p>
                    </div>

                    <div className="lv-case-obj" style={{ background: 'rgba(47, 123, 255, 0.08)', border: '1px solid rgba(47, 123, 255, 0.25)', borderRadius: '8px', padding: '9px 12px' }}>
                      <b style={{ display: 'block', fontSize: '10px', textTransform: 'uppercase', color: '#7FB0FF', letterSpacing: '0.06em', marginBottom: '3px' }}>{t('simulation.your_objective', undefined, 'Your Objective')}</b>
                      <span id="liveGuidanceObjective" style={{ fontSize: '12px', color: '#FFFFFF', lineHeight: '1.45' }}>
                        {currentStage.objective || simData.objectives?.[0] || 'Communicate effectively and resolve the customer inquiry.'}
                      </span>
                    </div>

                    {/* Scenario Stages & Tasks Completion */}
                    {stages.length > 1 && (
                      <div style={{ marginTop: '2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <b style={{ fontSize: '10px', textTransform: 'uppercase', color: '#7FB0FF', letterSpacing: '0.06em' }}>
                            {t('simulation.stage_breakdown', undefined, 'Scenario Stages')}
                          </b>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#38BDF8' }}>
                            Done: {currentStageIdx} of {stages.length}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          {stages.map((st: any, sIdx: number) => {
                            const isDone = sIdx < currentStageIdx;
                            const isCurrent = sIdx === currentStageIdx;
                            return (
                              <div
                                key={sIdx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  background: isCurrent ? 'rgba(56, 189, 248, 0.12)' : isDone ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255,255,255,0.03)',
                                  border: isCurrent ? '1px solid rgba(56, 189, 248, 0.35)' : isDone ? '1px solid rgba(34, 197, 94, 0.25)' : '1px solid rgba(255,255,255,0.06)',
                                }}
                              >
                                <span style={{ fontSize: '11.5px', color: isCurrent ? '#FFFFFF' : isDone ? '#86EFAC' : '#94A3B8', fontWeight: isCurrent ? 600 : 400 }}>
                                  {sIdx + 1}. {st.name || `Stage ${sIdx + 1}`}
                                </span>
                                <span
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    background: isDone ? 'rgba(34, 197, 94, 0.2)' : isCurrent ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.06)',
                                    color: isDone ? '#4ADE80' : isCurrent ? '#38BDF8' : '#64748B',
                                  }}
                                >
                                  {isDone ? '✓ Done' : isCurrent ? '● In Progress' : '○ Up Next'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Participating Personas Section */}
                    {personasList.length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <b style={{ display: 'block', fontSize: '10px', textTransform: 'uppercase', color: '#7FB0FF', letterSpacing: '0.06em', marginBottom: '6px' }}>
                          {t('simulation.scenario_personas', undefined, 'Scenario Personas')} ({personasList.length})
                        </b>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {personasList.map((p, idx) => (
                            <div
                              key={p.id || idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 8px',
                                borderRadius: '6px',
                                background: activePersonaIdx === idx ? 'rgba(47, 123, 255, 0.12)' : 'rgba(255,255,255,0.03)',
                                border: activePersonaIdx === idx ? '1px solid rgba(47, 123, 255, 0.3)' : '1px solid rgba(255,255,255,0.06)',
                              }}
                            >
                              <div
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  background: '#0E7C86',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  color: '#fff',
                                }}
                              >
                                {p.avatarUrl ? (
                                  <img src={p.avatarUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  p.name.slice(0, 2).toUpperCase()
                                )}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {p.name}
                                </div>
                                <div style={{ fontSize: '10.5px', color: '#9AABCB', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {translateRole(p.role, t)} {p.mood && p.mood !== 'Neutral' ? `· ${translateMood(p.mood, t)}` : ''}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Live Transcript Header */}
              <div className="lv-sh" style={{ flexShrink: 0 }}>
                <b>{t('simulation.live_transcript', undefined, 'Live transcript')}</b>
                <span className="lv-livep"><i />{t('simulation.live_badge', undefined, 'Live')}</span>
              </div>

              {/* Transcript Turns List */}
              <div
                className="lv-tr"
                id="transcriptMessagesList"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: '10px',
                  padding: '12px 16px',
                }}
              >
                {session.turns.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--mut)', fontSize: '12.5px' }}>
                    {t('simulation.transcript_listening', undefined, 'Listening for conversation start...')}
                  </div>
                )}
                {session.turns.map((turn: Turn, idx: number) => {
                  const isLearner = ['learner', 'user', 'me', 'student'].includes((turn.speaker || (turn as any).role || '').toLowerCase());
                  const turnPersonaName = (turn as any).personaName || (turn as any).speakerName;
                  const matchedPersona = personasList.find(
                    (p) => turnPersonaName && p.name.toLowerCase() === turnPersonaName.toLowerCase()
                  ) || personasList[activePersonaIdx] || personasList[0];

                  const speakerInitials = isLearner
                    ? userInitials
                    : (matchedPersona?.name || personaName).slice(0, 2).toUpperCase();
                  const speakerTag = isLearner
                    ? userName
                    : (matchedPersona?.name || personaName);
                  const roleTag = isLearner
                    ? t('simulation.role_learner', undefined, 'Learner')
                    : t('simulation.role_ai_prefix', { role: translateRole(matchedPersona?.role || personaRole, t) }, `AI · ${matchedPersona?.role || personaRole}`);
                  const formattedTimeStr = new Date(turn.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

                  return (
                    <div key={idx} className={`lv-msg ${isLearner ? 'lv-me' : ''}`} style={{ flexShrink: 0 }}>
                      <div className="lv-h">
                        <span className="lv-dot" style={{ background: isLearner ? '#4B5563' : '#0E7C86' }}>
                          {speakerInitials}
                        </span>
                        <b>{speakerTag}</b>
                        <span>{roleTag}</span>
                        <time>{formattedTimeStr}</time>
                        {!isLearner && (
                          <button
                            type="button"
                            className="lv-mrep"
                            title={session.isAudioLoading ? 'Audio is loading...' : session.isAiSpeaking ? 'Audio is playing...' : t('simulation.repeat_message_tooltip', undefined, 'Repeat this message')}
                            disabled={session.isAudioLoading || session.isAiSpeaking}
                            onClick={() => {
                              if (session.isAudioLoading || session.isAiSpeaking) return;
                              if (turn.text) {
                                handleRepeatAiTurn(turn.text, turn.audioUrl, turn.personaName);
                              }
                            }}
                            style={{
                              opacity: (session.isAudioLoading || session.isAiSpeaking) ? 0.45 : 1,
                              cursor: (session.isAudioLoading || session.isAiSpeaking) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 12a8 8 0 1 0 2.4-5.7" />
                              <path d="M4 4v4h4" />
                              <path d="m10.5 9.5 4 2.5-4 2.5z" fill="currentColor" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <p
                        id={!isLearner ? "liveAiSpokenText" : "liveLearnerSpokenText"}
                        className="lv-turn-text"
                        style={{
                          margin: 0,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: 1.5,
                          maxHeight: 'none',
                          overflow: 'visible',
                          display: 'block',
                          WebkitLineClamp: 'unset',
                        }}
                      >
                        {turn.text}
                      </p>
                    </div>
                  );
                })}
                {activeLearnerSpeech && (
                  <div className="lv-msg lv-me lv-live" style={{ flexShrink: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <div className="lv-h">
                      <span className="lv-dot" style={{ background: '#2563EB' }}>
                        {userInitials}
                      </span>
                      <b>{userName}</b>
                      <span>
                        {session.silenceCountdownSeconds !== null && session.silenceCountdownSeconds > 0
                          ? t('simulation.submitting_countdown', { seconds: session.silenceCountdownSeconds }, `Submitting in ${session.silenceCountdownSeconds}s...`)
                          : t('simulation.live_learner_speaking', undefined, 'Speaking...')}
                      </span>
                    </div>
                      <p
                        id="liveLearnerSpokenText"
                        className="lv-turn-text"
                        style={{
                          margin: 0,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: 1.5,
                          color: '#FFFFFF',
                          maxHeight: 'none',
                          overflow: 'visible',
                          display: 'block',
                          WebkitLineClamp: 'unset',
                        }}
                      >
                        {activeLearnerSpeech}
                      </p>
                      {session.silenceCountdownSeconds !== null && session.silenceCountdownSeconds > 0 && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '11.5px', color: '#93C5FD', fontWeight: 600 }}>
                            ⏱️ {t('simulation.submitting_countdown', { seconds: session.silenceCountdownSeconds }, `${session.silenceCountdownSeconds}s`)}
                          </span>
                          <button
                            type="button"
                            id="transcriptSubmitResponseBtn"
                            onClick={session.handleInstantSubmit}
                            style={{
                              background: '#0F8A5F',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '5px 12px',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            {t('simulation.submit_response', undefined, 'Submit Response')}
                          </button>
                        </div>
                      )}
                  </div>
                )}
                <div ref={transcriptEndRef} />
              </div>
            </aside>
          </div>

          {/* Text Input fallback when learner chooses manual text mode OR when microphone is blocked */}
          {(showManualTextInput || session.micPermissionDenied) && (
            <div
              id="simulationQuickInputBar"
              style={{
                padding: '8px 20px',
                background: 'rgba(5, 14, 34, 0.96)',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                /* Stacking is owned by index.css (#live-stage #simulationQuickInputBar) */
              }}
            >
              <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {session.micPermissionDenied && (
                  <span
                    style={{
                      fontSize: '11.5px',
                      fontWeight: 600,
                      color: '#FDE68A',
                      background: 'rgba(245, 158, 11, 0.15)',
                      border: '1px solid rgba(245, 158, 11, 0.35)',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                    className="lv-textmode-badge"
                  >
                    ⌨️ {t('simulation.text_mode_active', undefined, 'Text Mode Active')}
                  </span>
                )}
                <input
                  type="text"
                  id="manualTurnInput"
                  placeholder={
                    session.isAiSpeaking
                      ? t('simulation.text_input_ai_speaking', { name: personaName ? personaName.split(' ')[0] : 'AI' }, `${personaName ? personaName.split(' ')[0] : 'AI'} is speaking...`)
                      : session.state === 'processing'
                        ? t('simulation.text_input_ai_thinking', undefined, 'AI persona is evaluating...')
                        : t('simulation.text_input_placeholder', undefined, 'Type your response here and press Enter...')
                  }
                  value={session.manualText}
                  onChange={(e) => session.setManualText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      session.handleInstantSubmit();
                    }
                  }}
                  disabled={session.isAiSpeaking || session.state === 'processing'}
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.14)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    fontSize: '16px',
                    color: '#FFFFFF',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <button
                type="button"
                id="sendManualTurnBtn"
                onClick={session.handleInstantSubmit}
                disabled={(!session.manualText.trim() && !activeLearnerSpeech) || session.isAiSpeaking || session.state === 'processing'}
                style={{
                  background: (!session.manualText.trim() && !activeLearnerSpeech) || session.isAiSpeaking || session.state === 'processing'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 18px',
                  minHeight: '44px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: (!session.manualText.trim() && !activeLearnerSpeech) || session.isAiSpeaking || session.state === 'processing'
                    ? 'not-allowed'
                    : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: (!session.manualText.trim() && !activeLearnerSpeech) || session.isAiSpeaking || session.state === 'processing' ? 0.45 : 1,
                  boxShadow: (!session.manualText.trim() && !activeLearnerSpeech) || session.isAiSpeaking || session.state === 'processing'
                    ? 'none'
                    : '0 2px 8px rgba(37,99,235,0.4)',
                  transition: 'all 0.15s ease',
                }}
              >
                <span>{t('simulation.text_send', undefined, 'Send')}</span>
                <svg style={{ width: '13px', height: '13px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => setShowManualTextInput(false)}
                title={t('simulation.switch_to_voice_title', undefined, 'Switch back to voice mode')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94A3B8',
                  padding: '8px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* ================================================================ */}
          {/* Bottom Controls Bar (.lv-ctrls) */}
          {/* ================================================================ */}
          <footer className="lv-ctrls">
            <div className="lv-ctrls-meta">
              <div className="lv-left">
                <div className="lv-people" id="liveParticipants">
                  {personasList.map((p, idx) => (
                    <span
                      key={p.id || idx}
                      id={`pAvatarAi-${idx}`}
                      title={`${p.name} (${p.role})`}
                      style={{
                        background: '#0E7C86',
                        overflow: 'hidden',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {p.avatarUrl ? (
                        <img src={p.avatarUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        p.name.slice(0, 2).toUpperCase()
                      )}
                    </span>
                  ))}
                  <span id="pAvatarYou" style={{ background: '#4B5563' }}>{userInitials}</span>
                </div>
                <span id="liveParticipantsText" className="lv-participants-text">
                  <span className="lv-part-full">
                    {personasList.length === 1
                      ? t('simulation.participants_single', { name: personasList[0].name }, `1 AI agent (${personasList[0].name}) + you`)
                      : t('simulation.participants_multiple', { count: personasList.length }, `${personasList.length} AI personas + you`)}
                  </span>
                  <span className="lv-part-short">
                    {personasList.length === 1
                      ? `${personasList[0].name.split(' ')[0]} + ${t('simulation.you_label', undefined, 'You')}`
                      : `${personasList.length} AI + ${t('simulation.you_label', undefined, 'You')}`}
                  </span>
                </span>
              </div>

              <div className="lv-rightc" id="liveAutosaveStatus">
                <span className="lv-autosave-dot" />
                <span className="lv-autosave-full">{t('simulation.auto_saved_long', undefined, 'Auto-saved · resume if disconnected')}</span>
                <span className="lv-autosave-short">{t('simulation.auto_saved_short', undefined, 'Auto-saved')}</span>
              </div>
            </div>

            <div className="lv-ctrls-actions">
              <div className="lv-cbw">
                <button
                  type="button"
                  className={`lv-cb ${!session.isMuted && !session.micPermissionDenied ? 'lv-on' : ''}`}
                  id="toggleMicBtn"
                  title={session.micPermissionDenied ? t('simulation.retry_mic', undefined, 'Retry Mic') : session.isMuted ? t('simulation.unmute_mic', undefined, 'Unmute Mic') : t('simulation.mute_mic', undefined, 'Mute Mic')}
                  onClick={async () => {
                    if (session.micPermissionDenied) {
                      const ok = await session.retryMicAccess();
                      if (!ok) {
                        setShowMicHelp(true);
                      }
                      return;
                    }
                    if (session.isMuted) {
                      // USER REQUIREMENT: If I unmute the mic... TTS stops speaking immediately!
                      session.audio.stopSpeaking();
                      stopAllSpeech();
                      if (typeof window !== 'undefined' && window.speechSynthesis) {
                        try {
                          window.speechSynthesis.cancel();
                        } catch { }
                      }
                      if (typeof session.startListening === 'function') {
                        await session.startListening();
                      } else {
                        session.toggleMute();
                      }
                    } else {
                      session.toggleMute();
                    }
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="11" rx="3" />
                    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                  </svg>
                </button>
                <small id="micStatusLabel">
                  {session.micPermissionDenied
                    ? t('simulation.retry_mic', undefined, 'Retry Mic')
                    : session.isMuted
                      ? t('simulation.unmute_mic', undefined, 'Unmute')
                      : t('simulation.mute_mic', undefined, 'Mute')}
                </small>
              </div>

              <div className="lv-cbw">
                <button
                  type="button"
                  className={`lv-cb ${isCameraOn ? 'lv-on' : ''}`}
                  id="toggleCameraBtn"
                  title={t('simulation.toggle_camera_tooltip', undefined, 'Toggle Camera')}
                  onClick={() => setIsCameraOn(!isCameraOn)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="6" width="13" height="12" rx="2" />
                    <path d="m16 10 5-3v10l-5-3" />
                  </svg>
                </button>
                <small id="camStatusLabel">
                  <span className="lv-lbl-full">{isCameraOn ? t('simulation.camera_on', undefined, 'Camera on') : t('simulation.camera_off', undefined, 'Camera off')}</span>
                  <span className="lv-lbl-short">{isCameraOn ? t('simulation.cam_on_short', undefined, 'Cam on') : t('simulation.cam_off_short', undefined, 'Cam off')}</span>
                </small>
              </div>

              <div className="lv-cbw">
                <button
                  type="button"
                  className="lv-cb lv-hl"
                  id="repeatLastBtn"
                  title={session.isAudioLoading ? 'Audio is loading...' : session.isAiSpeaking ? 'Audio is playing...' : t('simulation.repeat_last_tooltip', undefined, 'Repeat last message')}
                  disabled={session.isAudioLoading || session.isAiSpeaking}
                  onClick={handleRepeatLatestAiTurn}
                  style={{
                    opacity: (session.isAudioLoading || session.isAiSpeaking) ? 0.45 : 1,
                    cursor: (session.isAudioLoading || session.isAiSpeaking) ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12a8 8 0 1 0 2.4-5.7" />
                    <path d="M4 4v4h4" />
                    <path d="m10.5 9.5 4 2.5-4 2.5z" fill="currentColor" />
                  </svg>
                </button>
                <small>
                  <span className="lv-lbl-full">{t('simulation.repeat_last', undefined, 'Repeat last')}</span>
                  <span className="lv-lbl-short">{t('simulation.repeat', undefined, 'Repeat')}</span>
                </small>
              </div>

              <div className="lv-cbw">
                <button
                  type="button"
                  className={`lv-cb ${isTranscriptDrawerOpen ? 'lv-on' : ''}`}
                  id="toggleTranscriptDrawerBtn"
                  title={t('simulation.transcript_tooltip', undefined, 'View full conversation transcript')}
                  onClick={() => setIsTranscriptDrawerOpen(!isTranscriptDrawerOpen)}
                  style={{ position: 'relative' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <line x1="8" y1="9" x2="16" y2="9" />
                    <line x1="8" y1="13" x2="13" y2="13" />
                  </svg>
                  {session.turns.length > 0 && (
                    <span
                      id="transcriptTurnsBadge"
                      style={{
                        position: 'absolute',
                        top: '-3px',
                        right: '-3px',
                        background: '#0D4FBD',
                        color: '#FFFFFF',
                        fontSize: '9.5px',
                        fontWeight: 800,
                        borderRadius: '999px',
                        padding: '1px 5px',
                        border: '1.5px solid #081636',
                        minWidth: '15px',
                        textAlign: 'center',
                        lineHeight: '1.3',
                      }}
                    >
                      {session.turns.length}
                    </span>
                  )}
                </button>
                <small id="transcriptDrawerLabel">{t('simulation.transcript', undefined, 'Transcript')}</small>
              </div>

              <div className="lv-cbw">
                <button
                  type="button"
                  className="lv-cb lv-end lv-end-btn"
                  id="endSimulationBtn"
                  title={t('simulation.end_assessment_tooltip', undefined, 'End assessment')}
                  onClick={() => {
                    session.audio.stopSpeaking();
                    stopAllSpeech();
                    if (typeof window !== 'undefined' && window.speechSynthesis) {
                      try {
                        window.speechSynthesis.cancel();
                      } catch { }
                    }
                    setShowEndModal(true);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                  </svg>
                </button>
                <small id="endStatusLabel">
                  <span className="lv-lbl-full">{t('simulation.end_assessment', undefined, 'End assessment')}</span>
                  <span className="lv-lbl-short">{t('simulation.end_short', undefined, 'End')}</span>
                </small>
              </div>
            </div>
          </footer>

          {/* ================================================================ */}
          {/* Mobile & Tablet Full Conversation Transcript Drawer */}
          {/* ================================================================ */}
          {isTranscriptDrawerOpen && (
            <div
              className="simulation-transcript-drawer-overlay"
              id="transcriptDrawerOverlay"
              role="dialog"
              aria-modal="true"
              aria-label={t('simulation.transcript', undefined, 'Conversation Transcript')}
              onClick={() => setIsTranscriptDrawerOpen(false)}
            >
              <div
                className="simulation-transcript-drawer-content"
                id="transcriptDrawerContent"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drag handle */}
                <div
                  style={{ padding: '10px 0 4px', display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
                  onClick={() => setIsTranscriptDrawerOpen(false)}
                >
                  <div style={{ width: '40px', height: '4px', borderRadius: '999px', background: 'rgba(255, 255, 255, 0.28)' }} />
                </div>

                {/* Drawer Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 18px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '8px',
                        background: 'rgba(37, 99, 235, 0.25)',
                        border: '1px solid rgba(59, 130, 246, 0.35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#60A5FA',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '15.5px', fontWeight: 700, color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Conversation Transcript
                        <span
                          id="drawerTurnsCountBadge"
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '999px',
                            background: 'rgba(56, 189, 248, 0.16)',
                            color: '#38BDF8',
                            border: '1px solid rgba(56, 189, 248, 0.3)',
                          }}
                        >
                          {session.turns.length} {session.turns.length === 1 ? 'Turn' : 'Turns'}
                        </span>
                      </h3>
                      <p style={{ margin: 0, fontSize: '11.5px', color: '#94A3B8' }}>
                        Live dialogue turns with AI persona
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    id="closeTranscriptDrawerBtn"
                    onClick={() => setIsTranscriptDrawerOpen(false)}
                    aria-label="Close Transcript Drawer"
                    style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '50%',
                      width: '38px',
                      height: '38px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#C9D2E3',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                {/* Drawer Messages Scroll Area */}
                <div
                  id="transcriptDrawerMessagesList"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {session.turns.length === 0 && !activeLearnerSpeech && (
                    <div style={{ textAlign: 'center', padding: '40px 16px', color: '#94A3B8' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>💬</div>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#E2E8F0', margin: '0 0 4px' }}>
                        {t('simulation.ready_to_begin', undefined, 'Ready to begin dialogue')}
                      </p>
                      <p style={{ fontSize: '12px', margin: 0, color: '#64748B' }}>
                        {t('simulation.ready_to_begin_desc', undefined, 'Speak through your microphone or type a response in the quick input box below.')}
                      </p>
                    </div>
                  )}

                  {session.turns.map((turn: Turn, idx: number) => {
                    const isLearner = ['learner', 'user', 'me', 'student'].includes((turn.speaker || (turn as any).role || '').toLowerCase());
                    const turnPersonaName = (turn as any).personaName || (turn as any).speakerName;
                    const matchedPersona = personasList.find(
                      (p) => turnPersonaName && p.name.toLowerCase() === turnPersonaName.toLowerCase()
                    ) || personasList[activePersonaIdx] || personasList[0];

                    const speakerInitials = isLearner
                      ? userInitials
                      : (matchedPersona?.name || personaName).slice(0, 2).toUpperCase();
                    const speakerTag = isLearner
                      ? userName
                      : (matchedPersona?.name || personaName);
                    const roleTag = isLearner
                      ? 'Learner'
                      : `AI · ${matchedPersona?.role || personaRole}`;
                    const formattedTimeStr = new Date(turn.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

                    return (
                      <div
                        key={idx}
                        className={`drawer-msg-card ${isLearner ? 'drawer-msg-learner' : 'drawer-msg-ai'}`}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          flexShrink: 0,
                          padding: '12px 14px',
                          borderRadius: '12px',
                          background: isLearner ? 'rgba(30, 58, 138, 0.28)' : 'rgba(255, 255, 255, 0.05)',
                          border: isLearner ? '1px solid rgba(59, 130, 246, 0.35)' : '1px solid rgba(255, 255, 255, 0.08)',
                          alignSelf: isLearner ? 'flex-end' : 'flex-start',
                          maxWidth: '92%',
                          width: 'fit-content',
                          minWidth: 'min(220px, 92%)',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                        }}
                      >
                        <div className="drawer-msg-card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', rowGap: '2px', flexWrap: 'wrap', minWidth: 0 }}>
                          <div className="drawer-msg-id" style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 0%', minWidth: 0 }}>
                            <span
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: isLearner ? '#2563EB' : '#0E7C86',
                                color: '#FFFFFF',
                                fontSize: '11px',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {speakerInitials}
                            </span>
                            <b style={{ fontSize: '13px', color: isLearner ? '#93C5FD' : '#7FB0FF', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{speakerTag}</b>
                            <span style={{ fontSize: '11px', color: '#94A3B8', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleTag}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <time style={{ fontSize: '10.5px', color: '#64748B' }}>{formattedTimeStr}</time>
                            {!isLearner && (
                              <button
                                type="button"
                                title={session.isAudioLoading ? 'Audio is loading...' : session.isAiSpeaking ? 'Audio is playing...' : 'Replay AI turn audio'}
                                disabled={session.isAudioLoading || session.isAiSpeaking}
                                onClick={() => {
                                  if (session.isAudioLoading || session.isAiSpeaking) return;
                                  if (turn.text) {
                                    handleRepeatAiTurn(turn.text, turn.audioUrl, turn.personaName);
                                  }
                                }}
                                style={{
                                  background: 'rgba(56, 189, 248, 0.12)',
                                  border: '1px solid rgba(56, 189, 248, 0.25)',
                                  borderRadius: '6px',
                                  color: '#38BDF8',
                                  cursor: (session.isAudioLoading || session.isAiSpeaking) ? 'not-allowed' : 'pointer',
                                  opacity: (session.isAudioLoading || session.isAiSpeaking) ? 0.45 : 1,
                                  padding: '3px 6px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                                </svg>
                                <span>{t('simulation.replay', undefined, 'Replay')}</span>
                              </button>
                            )}
                          </div>
                        </div>
                        <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.45', color: '#F1F5F9', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {turn.text}
                        </p>
                      </div>
                    );
                  })}

                  {activeLearnerSpeech && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        background: 'rgba(34, 197, 94, 0.12)',
                        border: '1px solid rgba(34, 197, 94, 0.35)',
                        alignSelf: 'flex-end',
                        width: '92%',
                        maxWidth: '92%',
                        minWidth: 'min(220px, 92%)',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 8px #22C55E', flexShrink: 0 }} />
                          <b style={{ fontSize: '13px', color: '#4ADE80' }}>
                            {session.silenceCountdownSeconds !== null && session.silenceCountdownSeconds > 0
                              ? `You (${session.silenceCountdownSeconds}s)`
                              : 'You (speaking...)'}
                          </b>
                        </div>
                        {session.silenceCountdownSeconds !== null && session.silenceCountdownSeconds > 0 && (
                          <button
                            type="button"
                            id="drawerSubmitResponseBtn"
                            onClick={session.handleInstantSubmit}
                            style={{
                              background: '#0F8A5F',
                              color: '#FFFFFF',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '5px 12px',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              minHeight: '36px',
                            }}
                          >
                            {t('simulation.submit_response', undefined, 'Submit Response')}
                          </button>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.45', color: '#FFFFFF', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {activeLearnerSpeech}
                      </p>
                    </div>
                  )}

                  <div ref={transcriptDrawerEndRef} />
                </div>

                {/* Drawer Footer */}
                <div
                  style={{
                    padding: '12px 18px calc(12px + env(safe-area-inset-bottom, 0px))',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    background: '#071536',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <button
                    type="button"
                    id="returnToStageBtn"
                    onClick={() => setIsTranscriptDrawerOpen(false)}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.08)',
                      color: '#FFFFFF',
                      border: '1px solid rgba(255, 255, 255, 0.16)',
                      borderRadius: '10px',
                      padding: '11px 16px',
                      fontSize: '13.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minHeight: '44px',
                    }}
                  >
                    <span>{t('simulation.return_to_stage', undefined, 'Return to Simulation Stage')}</span>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* End Assessment Confirmation Modal */}
          {/* ================================================================ */}
          {showEndModal && (
            <div className="lv-modal" id="endSimModal" style={{ display: 'flex' }}>
              <div className="lv-dialog">
                <h3>{t('simulation.end_modal_title', undefined, 'End this simulation?')}</h3>
                <p>
                  {t('simulation.end_modal_desc', undefined, "Your audio, video, and transcript will be submitted for scoring and feedback. You won't be able to continue this attempt.")}
                </p>
                <div className="lv-stat">
                  <div>
                    <span>{t('simulation.end_modal_time_elapsed', undefined, 'Time elapsed')}</span>
                    <b id="endModalElapsed">{formattedElapsed}</b>
                  </div>
                  <div>
                    <span>{t('simulation.end_modal_turns_recorded', undefined, 'Turns recorded')}</span>
                    <b id="endModalTurns">{t('simulation.end_modal_turns_count', { count: session.turns.length }, `${session.turns.length} turns`)}</b>
                  </div>
                </div>
                <div className="lv-drow">
                  <button type="button" id="cancelEndSimBtn" onClick={() => setShowEndModal(false)}>
                    {t('simulation.end_modal_continue', undefined, 'Continue simulation')}
                  </button>
                  <button type="button" className="lv-go" id="confirmEndSimBtn" onClick={handleEndSimulationConfirm}>
                    {t('simulation.end_modal_submit', undefined, 'End and submit')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* Turn Ended / Conversation Complete Popup Modal */}
          {/* ================================================================ */}
          {session.turnEndedModal?.isOpen && (
            <div className="lv-modal" id="turnEndedModal" style={{ display: 'flex' }}>
              <div className="lv-dialog">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: session.turnEndedModal.reason === 'TURN_LIMIT_REACHED' ? 'var(--warn-soft)' : 'var(--ok-soft)',
                      color: session.turnEndedModal.reason === 'TURN_LIMIT_REACHED' ? 'var(--warn)' : 'var(--ok)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {session.turnEndedModal.reason === 'TURN_LIMIT_REACHED' ? '⏱' : '✓'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18 }}>{session.turnEndedModal.reason === 'TURN_LIMIT_REACHED' ? t('simulation.turn_ended_limit_title', undefined, 'Turn Limit Reached') : t('simulation.turn_ended_complete_title', undefined, 'Conversation Complete')}</h3>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {session.turnEndedModal.reason === 'TURN_LIMIT_REACHED'
                        ? t('simulation.turn_ended_limit_title', undefined, 'Simulation turn threshold reached')
                        : t('simulation.turn_ended_complete_title', undefined, 'Conversation concluded successfully')}
                    </span>
                  </div>
                </div>

                <p style={{ margin: '4px 0 0 0', lineHeight: 1.5 }}>
                  {session.turnEndedModal.reason === 'TURN_LIMIT_REACHED'
                    ? t('simulation.turn_ended_limit_desc', { current: session.turnEndedModal.turnsCount, max: session.turnEndedModal.maxTurns ?? '' }, session.turnEndedModal.description)
                    : t('simulation.turn_ended_concluded_desc', { count: session.turnEndedModal.turnsCount }, session.turnEndedModal.description)}
                </p>

                <div className="lv-stat">
                  <div>
                    <span>{t('simulation.end_modal_time_elapsed', undefined, 'Time elapsed')}</span>
                    <b id="turnEndedModalElapsed">{formattedElapsed}</b>
                  </div>
                  <div>
                    <span>{t('simulation.end_modal_turns_recorded', undefined, 'Turns completed')}</span>
                    <b id="turnEndedModalTurns">
                      {session.turnEndedModal.turnsCount}
                      {session.turnEndedModal.maxTurns ? ` / ${session.turnEndedModal.maxTurns}` : ''} {t('simulation.end_modal_turns_count', { count: '' }, 'turns')}
                    </b>
                  </div>
                </div>

                <div className="lv-drow" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    id="reviewTranscriptTurnEndedBtn"
                    onClick={() => {
                      session.setActiveTab('transcript');
                      session.setTurnEndedModal(prev => prev ? { ...prev, isOpen: false } : null);
                    }}
                  >
                    {t('simulation.transcript', undefined, 'Review transcript')}
                  </button>
                  <button
                    type="button"
                    className="lv-primary-btn"
                    id="confirmTurnEndedBtn"
                    style={{ background: 'var(--blue)', color: '#fff', borderColor: 'var(--blue)' }}
                    onClick={handleEndSimulationConfirm}
                  >
                    {t('simulation.turn_ended_view_report', undefined, 'View evaluation results')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sticky Completion Action Banner if user minimizes turn-ended modal to review transcript */}
          {session.turnEndedModal && !session.turnEndedModal.isOpen && (
            <div
              id="turnEndedBanner"
              style={{
                position: 'fixed',
                bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 900,
                background: 'var(--navy)',
                color: '#fff',
                padding: '10px 18px',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(7, 29, 69, 0.35)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                maxWidth: '90vw'
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.3 }}>
                <b>
                  {t('simulation.turns_completed_summary', { count: session.turns.length }, `Simulation turns completed (${session.turns.length} turns).`)}
                </b>
                <span style={{ color: 'var(--chrome-text)', marginLeft: 6 }}>
                  {t('simulation.ready_for_debrief', undefined, 'Ready for evaluation debrief.')}
                </span>
              </div>
              <button
                type="button"
                id="turnEndedBannerViewResultsBtn"
                onClick={handleEndSimulationConfirm}
                style={{
                  background: 'var(--blue)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {t('simulation.turn_ended_view_report', undefined, 'View evaluation results →')}
              </button>
            </div>
          )}

          {/* ================================================================ */}
          {/* Live LLM API Trace Modal */}
          {/* ================================================================ */}
          {showTraceModal && (
            <div
              className="lv-modal"
              style={{
                display: 'flex',
                zIndex: 1000,
                background: 'rgba(5, 10, 20, 0.85)',
                backdropFilter: 'blur(8px)',
                padding: '16px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setShowTraceModal(false)}
            >
              <div
                className="lv-dialog"
                style={{
                  maxWidth: '820px',
                  width: '95vw',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#0B1120',
                  border: '1px solid #1E293B',
                  borderRadius: '16px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
                  overflow: 'hidden',
                  padding: 0,
                  textAlign: 'left',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    background: '#0F172A',
                    borderBottom: '1px solid #1E293B',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#10B981',
                        animation: 'pulse 1.5s infinite',
                        boxShadow: '0 0 10px #10B981',
                      }}
                    />
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#F8FAFC', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🔍 Real-Time LLM API & Runtime Trace
                    </h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const fullPayload = {
                          scenarioId: targetScenarioId,
                          attemptId: session.attemptId,
                          currentStage: currentStageName,
                          persona: { name: personaName, role: personaRole, mood: personaMood },
                          language: languageLabel,
                          turnsCount: session.turns.length,
                          turns: session.turns,
                          timestamp: new Date().toISOString(),
                        };
                        navigator.clipboard.writeText(JSON.stringify(fullPayload, null, 2));
                        setCopiedTrace(true);
                        setTimeout(() => setCopiedTrace(false), 2000);
                      }}
                      style={{
                        background: copiedTrace ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.15)',
                        border: copiedTrace ? '1px solid #10B981' : '1px solid rgba(56, 189, 248, 0.3)',
                        color: copiedTrace ? '#10B981' : '#38BDF8',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {copiedTrace ? '✓ Copied JSON' : '📋 Copy JSON'}
                    </button>
                    <button
                      type="button"
                      aria-label="Close trace modal"
                      onClick={() => setShowTraceModal(false)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#94A3B8',
                        borderRadius: '6px',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Content Area */}
                <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1, display: 'grid', gap: '14px' }}>
                  {/* Top 4 KPI metric chips */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                      gap: '10px',
                    }}
                  >
                    <div style={{ background: '#131D31', border: '1px solid #1E293B', padding: '10px 12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '10px', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>{t('simulation.trace_session_attempt', undefined, 'Session Attempt')}</div>
                      <div style={{ fontSize: '12.5px', color: '#38BDF8', fontFamily: 'monospace', fontWeight: 700, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {session.attemptId || 'Initializing...'}
                      </div>
                    </div>

                    <div style={{ background: '#131D31', border: '1px solid #1E293B', padding: '10px 12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '10px', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>{t('simulation.trace_target_persona', undefined, 'Target Persona')}</div>
                      <div style={{ fontSize: '12.5px', color: '#FDE68A', fontWeight: 700, marginTop: '2px' }}>
                        {personaName} · {translateRole(personaRole, t)}
                      </div>
                    </div>

                    <div style={{ background: '#131D31', border: '1px solid #1E293B', padding: '10px 12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '10px', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>{t('simulation.trace_stage_progression', undefined, 'Stage Progression')}</div>
                      <div style={{ fontSize: '12.5px', color: '#A7F3D0', fontWeight: 700, marginTop: '2px' }}>
                        {currentStageName} ({currentStageIdx + 1}/{stages.length})
                      </div>
                    </div>

                    <div style={{ background: '#131D31', border: '1px solid #1E293B', padding: '10px 12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '10px', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>{t('simulation.trace_language_turns', undefined, 'Language & Turns')}</div>
                      <div style={{ fontSize: '12.5px', color: '#E2E8F0', fontWeight: 700, marginTop: '2px' }}>
                        {languageLabel} · {t('simulation.trace_turns_count', { count: session.turns.length }, `${session.turns.length} turns`)}
                      </div>
                    </div>
                  </div>

                  {/* Dialogue Turn Exchange Section */}
                  <div style={{ background: '#0F172A', border: '1px solid #1E293B', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('simulation.trace_live_exchange_log', { count: session.turns.length }, `Live Turn Exchange Log (${session.turns.length} turns)`)}
                      </span>
                      <span style={{ fontSize: '10.5px', color: '#64748B' }}>{t('simulation.trace_realtime_stream', undefined, 'Real-time dialogue stream')}</span>
                    </div>

                    {session.turns.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '12px' }}>
                        {t('simulation.trace_no_turns', undefined, 'No turns processed yet. Say or type your greeting to initiate dialogue.')}
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px', maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                        {session.turns.map((t: any, idx: number) => {
                          const isCustomer = (t.speaker || '').toUpperCase() === 'PERSONA' || (t.speaker || '').toUpperCase() === 'AI' || (t.speaker || '').toUpperCase() === 'ASSISTANT';
                          return (
                            <div
                              key={idx}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                background: isCustomer ? 'rgba(245, 158, 11, 0.08)' : 'rgba(56, 189, 248, 0.08)',
                                border: isCustomer ? '1px solid rgba(245, 158, 11, 0.25)' : '1px solid rgba(56, 189, 248, 0.25)',
                                display: 'grid',
                                gap: '3px',
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: isCustomer ? '#F59E0B' : '#38BDF8' }}>
                                  #{idx + 1} {isCustomer ? `Customer (${personaName})` : 'Learner (You)'}
                                </span>
                                <span style={{ fontSize: '9.5px', color: '#64748B', fontFamily: 'monospace' }}>
                                  {t.timestamp || `${Math.round(idx * 15)}s`}
                                </span>
                              </div>
                              <p style={{ margin: 0, fontSize: '12px', color: '#F1F5F9', lineHeight: 1.4 }}>
                                {t.text}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Raw Engine JSON Payload */}
                  <div style={{ background: '#070C18', border: '1px solid #1E293B', borderRadius: '10px', padding: '12px 14px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                      Raw Telemetry Payload:
                    </div>
                    <pre
                      style={{
                        margin: 0,
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        color: '#93C5FD',
                        maxHeight: '140px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        lineHeight: 1.4,
                      }}
                    >
                      {JSON.stringify(
                        {
                          scenarioId: targetScenarioId,
                          attemptId: session.attemptId,
                          stage: { name: currentStageName, index: currentStageIdx + 1, total: stages.length },
                          persona: { name: personaName, role: personaRole, startingMood: personaMood },
                          language: languageLabel,
                          recentTurns: session.turns.slice(-3),
                        },
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </div>

                {/* Footer */}
                <div
                  style={{
                    padding: '12px 20px',
                    background: '#0F172A',
                    borderTop: '1px solid #1E293B',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowTraceModal(false)}
                    style={{
                      background: 'var(--blue, #0D4FBD)',
                      color: '#FFFFFF',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Close Trace
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* Pre-Flight System Readiness Check Modal */}
          {/* ================================================================ */}
          <SystemCheckModal
            isOpen={!isRetry && showPreflightModal && !preflightVerified && Boolean(session.briefing && (scenarioRequirements.requiresCamera || scenarioRequirements.requiresMicrophone || scenarioRequirements.requiresSpeaker))}
            scenarioTitle={scenarioTitle}
            requiresMicrophone={scenarioRequirements.requiresMicrophone ?? false}
            requiresCamera={scenarioRequirements.requiresCamera ?? false}
            requiresSpeaker={scenarioRequirements.requiresSpeaker ?? false}
            onCancel={() => {
              navigate(targetAttemptId ? '/attempts' : '/dashboard');
            }}
            onComplete={() => {
              try {
                if (typeof window !== 'undefined' && window.speechSynthesis) {
                  if (window.speechSynthesis.paused) {
                    window.speechSynthesis.resume();
                  }
                }
              } catch { }
              setPreflightVerified(true);
              setShowPreflightModal(false);
              if (scenarioRequirements.requiresCamera !== false) {
                setIsCameraOn(true);
              }
            }}
          />

          {/* ================================================================ */}
          {/* Post-Simulation Result Preparation & Evaluation Loader */}
          {/* ================================================================ */}
          {(isEndingSession || session.state === 'completing' || session.state === 'completed') && (
            <div
              id="evaluatingOverlay"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 99999,
                background: 'radial-gradient(ellipse at 50% 30%, #0A1E4A 0%, #050F26 80%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                color: '#FFFFFF',
                backdropFilter: 'blur(20px)',
              }}
            >
              <div
                style={{
                  maxWidth: '520px',
                  width: '100%',
                  background: 'rgba(15, 35, 80, 0.92)',
                  border: '1px solid rgba(14, 124, 134, 0.5)',
                  borderRadius: '24px',
                  padding: '40px 36px',
                  textAlign: 'center',
                  boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
                }}
              >
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14, 124, 134, 0.3) 0%, rgba(11, 47, 69, 0.8) 100%)',
                    border: '2px solid #0E7C86',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid rgba(255, 255, 255, 0.15)',
                      borderTopColor: '#38BDF8',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                </div>

                <h2
                  id="evaluatingOverlayTitle"
                  style={{
                    fontFamily: 'var(--display, Inter, sans-serif)',
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#FFFFFF',
                    margin: '0 0 12px',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {t('simulation.eval_overlay_title', undefined, 'Analyzing & Preparing Results')}
                </h2>

                <p
                  id="evaluatingOverlayDesc"
                  style={{
                    fontSize: '14px',
                    color: '#94A3B8',
                    margin: '0 0 28px',
                    lineHeight: 1.6,
                  }}
                >
                  {t(
                    'simulation.eval_overlay_desc',
                    undefined,
                    "Your simulation has ended. Trainova's multi-dimension assessment engine is synthesizing dialogue turns, assessing competencies, and preparing your comprehensive scorecard..."
                  )}
                </p>

                <div
                  id="evaluatingChecklist"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    textAlign: 'left',
                    background: 'rgba(5, 15, 38, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#38BDF8' }}>
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>✓</span>{' '}
                    <span>
                      {isAudioOnlyScenario
                        ? t('simulation.eval_mic_stopped', undefined, 'Microphone capture stopped')
                        : t('simulation.eval_mic_video_stopped', undefined, 'Microphone & video capture stopped')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#38BDF8' }}>
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>✓</span>{' '}
                    <span>
                      {t('simulation.eval_transcript_captured', { count: session.turns.length }, `Session transcript captured (${session.turns.length} turns)`)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#FFB547' }}>
                    <span style={{ fontSize: '14px' }}>⏳</span>{' '}
                    <span>
                      {t('simulation.eval_generating_scorecard', undefined, 'Generating rubric assessment & behavioral scorecard...')}
                    </span>
                  </div>
                </div>

                {showStuckExit && (
                  <div style={{ marginTop: '20px', paddingTop: '18px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <p style={{ fontSize: '12.5px', color: '#94A3B8', margin: '0 0 12px', lineHeight: 1.5 }}>
                      {t('simulation.eval_stuck_desc', undefined, 'This is taking longer than usual. You can keep waiting here, or open the attempt and read the scorecard once it finishes.')}
                    </p>
                    <button
                      type="button"
                      id="openAttemptWhileEvaluatingBtn"
                      onClick={() => navigate(effectiveAttemptId ? `/attempts/${effectiveAttemptId}/results` : '/attempts')}
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#E2E8F0',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '10px',
                        padding: '9px 18px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {t('simulation.eval_open_attempt_btn', undefined, 'Open my attempt →')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};
